import { useState, useEffect, useContext, useRef, useCallback, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../context/AuthContext';
import { useTierLimits } from '../context/TierLimitsContext';
import SEO from '../components/SEO';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import axios from 'axios';
import AudioFileTranscription from '../components/AudioFileTranscription';
import MicrophoneRecorder from '../components/MicrophoneRecorder';
import { DEFAULT_MODEL_ID } from '../config/modelConfig';
import AudioPlayerWithDuration from '../components/AudioPlayerWithDuration';
import { AUTOPLAY_MAX_CHAR_LENGTH } from '../utils/constants';
import { useWebSocketUpdates } from '../hooks/useWebSocketUpdates';
import { getAudioProxyUrl } from '../utils/audioProxy';
import { detectEmotionTags, voiceSupportsEmotionTags, classifyBracketMatches, stripUnsupportedBrackets } from '../utils/emotionTagDetector';
import { EmotionTagsConfirmModal } from '../components/EmotionTagsWarning';
import UnifiedExportModal from '../components/UnifiedExportModal';
import PlanCreditsDisplay from '../components/PlanCreditsDisplay';
import TranslationStep1Input, { TranslationStep2InputContent } from '../components/TranslationStep1Input';
import TranslationStep2Language from '../components/TranslationStep2Language';
import TranslationStep3TTS from '../components/TranslationStep3TTS';
import TranslationHistoryCard from '../components/TranslationHistoryCard';
import TranslateOptionsTab from '../components/TranslateOptionsTab';
import TranslateSettingsTab from '../components/TranslateSettingsTab';
import { useLiveStream } from '../hooks/useLiveStream';
import { splitTextForStreaming } from '../utils/textBatchSplitter';
import { TTS_DEFAULTS } from '../config/ttsDefaults';
import { pcmSegmentsToWavBlob } from '../utils/wavEncoder';
import { useLanguage } from '../context/LanguageContext';
import TRANSLATE from '../locales/translate';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { regionalModelIdOf } from '../utils/regionalModel'

// Default Creole Studio Pro voice used for Speech-to-Speech playback when
// the user hasn't picked one explicitly. Berthie (Forward) — active,
// ccl_ht_gem_pro-capable, tested end-to-end in the S2S smoke on 2026-07-02.
// The previous default 'smgykWMfupuWlPLWf0Zi9' didn't exist in the Voice
// table, so every S2S synth failed at the stream-audio step with
// "Voice ... not found for job".
const DEFAULT_S2S_CREOLE_VOICE_ID = 'R8AqIUPjP3sciYOOgnXXT';
const S2S_MODEL_ID = 'ccl_ht_gem_pro';

// iOS style toggle switch component
const IOSSwitch = ({ checked, onChange, disabled = false }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`
          w-11 h-6 rounded-full transition-colors duration-200 ease-in-out
          ${checked 
            ? 'bg-green-500' 
            : 'bg-gray-300 dark:bg-gray-600'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <div
          className={`
            absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md
            transition-transform duration-200 ease-in-out
            ${checked ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </div>
    </label>
  )
};

// Component for highlighting text as it's spoken
const HighlightedText = ({ text, highlightedWordIndex, wordTimings }) => {
  // If we have word timings, use them for accurate word boundaries
  if (wordTimings && wordTimings.length > 0) {
    return (
      <>
        {wordTimings.map((timing, index) => (
          <span
            key={index}
            className={
              index === highlightedWordIndex
                ? 'bg-yellow-300 dark:bg-yellow-600 text-gray-900 dark:text-gray-100 px-1 rounded transition-colors duration-200'
                : ''
            }
          >
            {timing.word}{index < wordTimings.length - 1 ? ' ' : ''}
          </span>
        ))}
      </>
    );
  }
  
  // Fallback to simple word splitting
  const words = text.split(/\s+/);
  
  return (
    <>
      {words.map((word, index) => (
        <span
          key={index}
          className={
            index === highlightedWordIndex
              ? 'bg-yellow-300 dark:bg-yellow-600 text-gray-900 dark:text-gray-100 px-1 rounded transition-colors duration-200'
              : ''
          }
        >
          {word}{index < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </>
  );
};

// Enhanced text sanitization
const sanitizeText = (text) => {
  if (!text) return '';
  
  let cleaned = text.normalize('NFKC');
  
  // Replace quotes and special characters
  cleaned = cleaned
    .replace(/[\u2018\u2019\u201A\u201B\u0060\u00B4]/g, "'")
    .replace(/[\u02BC\u02C8\u0301\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/[\u2013\u2014\u2015]/g, '-')
    .replace(/[\u2026]/g, '...')
    .replace(/[\u00A0]/g, ' ')
    .replace(/'/g, "'")
    .replace(/'/g, "'")
    .replace(/"/g, '"')
    .replace(/"/g, '"')
    .replace(/–/g, '-')
    .replace(/—/g, '-')
    .replace(/…/g, '...')
    .replace(/ /g, ' ');
  
  // Remove HTML/XML tags
  cleaned = cleaned.replace(/<[^>]+>/g, '');
  
  // Remove zero-width characters
  cleaned = cleaned
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u2060\u2061\u2062\u2063\u2064]/g, '');
  
  // Allow basic ASCII and common accented characters
  cleaned = cleaned.replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\u00FF\u0100-\u017F]/g, '');
  
  return cleaned;
};

// Helper function to format processing time
const formatProcessingTime = (ms) => {
  if (!ms) return 'N/A';
  
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

// Helper function to copy text to clipboard
const copyToClipboard = async (text, successMessage = 'Copied!') => {
  try {
    await navigator.clipboard.writeText(text);
    // You could add a toast notification here
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

// Helper function to format duration in seconds to human readable format
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Helper function to download audio
const downloadAudio = (audioUrl, filename) => {
  const link = document.createElement('a');
  link.href = audioUrl;
  link.download = filename || 'audio.wav';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Enhanced Download Modal Component (matching TTS page style)
const EnhancedDownloadModal = ({ job, translatedText, voiceName, onClose, onDownload }) => {
  const { language } = useLanguage();
  const t = TRANSLATE[language] || TRANSLATE.en;
  const dateLocale = { en: 'en-US', ht: 'fr-FR', fr: 'fr-FR', es: 'es-ES' }[language] || 'en-US';
  // Helper to get default settings from localStorage
  const getDefaultDownloadSettings = () => {
    try {
      const saved = localStorage.getItem('ttsDownloadPreferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          format: parsed.format || 'mp3',
          quality: parsed.quality || '44100',
          bitrate: parsed.bitrate || '192',
          includeMetadata: parsed.includeMetadata ?? true,
          normalizeVolume: parsed.normalizeVolume ?? false
        };
      }
    } catch (e) {
      console.error('Error loading download preferences:', e);
    }
    return {
      format: 'mp3',
      quality: '44100',
      bitrate: '192',
      includeMetadata: true,
      normalizeVolume: false
    };
  };

  const [downloadSettings, setDownloadSettings] = useState(() => {
    const defaults = getDefaultDownloadSettings();
    
    // Generate voice initials
    const getVoiceInitials = (name) => {
      if (!name) return 'xx';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toLowerCase();
      }
      return name.slice(0, 2).toLowerCase();
    };
    
    // Generate date in yyyy-mm-dd format
    const date = new Date();
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // Generate filename: translation-<voice-initial>-ht-yyyy-mm-dd
    const voiceInitials = getVoiceInitials(voiceName);
    const filename = `translation-${voiceInitials}-ht-${dateStr}`;
    
    return {
      filename,
      folder: 'Translations',
      ...defaults
    };
  });

  const isPaidUser = () => true; // Implement proper check if needed

  const formatDate = (dateString) => {
    if (!dateString) return t.dl_na;
    try {
      return new Date(dateString).toLocaleString(dateLocale);
    } catch (e) {
      return t.dl_na;
    }
  };

  const getEstimatedFileSize = () => {
    if (!job?.duration_seconds) return t.dl_unknown_size;
    
    const duration = job.duration_seconds;
    const sampleRate = parseInt(downloadSettings.quality);
    const bitrate = parseInt(downloadSettings.bitrate);
    
    let sizeInBytes;
    
    if (downloadSettings.format === 'wav') {
      sizeInBytes = sampleRate * 16 * 1 * duration / 8;
    } else if (downloadSettings.format === 'flac') {
      sizeInBytes = (sampleRate * 16 * 1 * duration / 8) * 0.6;
    } else {
      sizeInBytes = (bitrate * 1000 * duration) / 8;
    }
    
    if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  const handleDownload = async () => {
    // Save preferences
    const prefsToSave = {
      format: downloadSettings.format,
      quality: downloadSettings.quality,
      bitrate: downloadSettings.bitrate,
      includeMetadata: downloadSettings.includeMetadata,
      normalizeVolume: downloadSettings.normalizeVolume
    };
    localStorage.setItem('ttsDownloadPreferences', JSON.stringify(prefsToSave));
    
    // If we have a folder handle, we can save directly to that folder
    if (downloadSettings.folderHandle && 'showSaveFilePicker' in window) {
      try {
        // Pass the folder handle information to the parent
        onDownload({
          ...downloadSettings,
          useDirectoryHandle: true
        });
      } catch (err) {
        console.error('Error using folder handle:', err);
        // Fallback to regular download
        onDownload(downloadSettings);
      }
    } else {
      // Regular download without specific folder
      onDownload(downloadSettings);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold dark:text-gray-100">{t.dl_title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-6">
          {/* File Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.dl_file_info}</h4>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t.dl_voice_label}</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">{voiceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t.dl_duration_label}</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {formatDuration(job?.duration_seconds || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t.dl_created_label}</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {formatDate(job?.completed_at || job?.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t.dl_chars_label}</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {translatedText?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Download Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dl_location_label}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={downloadSettings.folder}
                onChange={(e) => setDownloadSettings(prev => ({ ...prev, folder: e.target.value }))}
                placeholder={t.dl_folder_placeholder}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <span className="self-center text-gray-500 dark:text-gray-400">/</span>
              <input
                type="text"
                value={downloadSettings.filename}
                onChange={(e) => setDownloadSettings(prev => ({ ...prev, filename: e.target.value }))}
                placeholder={t.dl_filename_placeholder}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t.dl_saved_as_prefix} {downloadSettings.folder}/{downloadSettings.filename}.{downloadSettings.format}
            </p>
          </div>

          {/* Format and Quality Settings */}
          <div className="grid grid-cols-2 gap-4">
            {/* Format selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.dl_audio_format_label}
              </label>
              <select
                value={downloadSettings.format}
                onChange={(e) => setDownloadSettings(prev => ({ ...prev, format: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="mp3">{t.dl_format_mp3}</option>
                <option value="wav">{t.dl_format_wav}</option>
                <option value="flac">{t.dl_format_flac}</option>
                <option value="m4a">{t.dl_format_m4a}</option>
                <option value="ogg">{t.dl_format_ogg}</option>
              </select>
            </div>

            {/* Quality selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.dl_sample_rate_label}
              </label>
              <select
                value={downloadSettings.quality}
                onChange={(e) => setDownloadSettings(prev => ({ ...prev, quality: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                {isPaidUser() ? (
                  <>
                    <option value="48000">{t.dl_rate_48000}</option>
                    <option value="44100">{t.dl_rate_44100}</option>
                    <option value="22050">{t.dl_rate_22050}</option>
                    <option value="16000">{t.dl_rate_16000}</option>
                    <option value="8000">{t.dl_rate_8000}</option>
                  </>
                ) : (
                  <>
                    <option value="22050">{t.dl_rate_22050}</option>
                    <option value="16000">{t.dl_rate_16000}</option>
                    <option value="8000">{t.dl_rate_8000}</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Bitrate for lossy formats */}
          {(downloadSettings.format === 'mp3' || downloadSettings.format === 'm4a' || downloadSettings.format === 'ogg') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.dl_bitrate_label}
              </label>
              <select
                value={downloadSettings.bitrate}
                onChange={(e) => setDownloadSettings(prev => ({ ...prev, bitrate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="320">{t.dl_bitrate_320}</option>
                <option value="256">{t.dl_bitrate_256}</option>
                <option value="192">{t.dl_bitrate_192}</option>
                <option value="128">{t.dl_bitrate_128}</option>
                <option value="96">{t.dl_bitrate_96}</option>
                <option value="64">{t.dl_bitrate_64}</option>
              </select>
            </div>
          )}

          {/* Additional Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dl_additional_options}
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={downloadSettings.includeMetadata}
                  onChange={(e) => setDownloadSettings(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t.dl_include_metadata}
                </span>
              </label>
              {isPaidUser() && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={downloadSettings.normalizeVolume || false}
                    onChange={(e) => setDownloadSettings(prev => ({ ...prev, normalizeVolume: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t.dl_normalize_volume}
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* File Size Estimate */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{t.dl_estimated_size}</strong> {getEstimatedFileSize()}
            </p>
          </div>
        </div>

        {/* Modal buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            {t.dl_cancel}
          </button>
          <button
            onClick={handleDownload}
            className="w-full sm:w-auto px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{t.dl_download_button}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Format character count with K/M notation
const formatCharCount = (count) => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  } else if (count >= 1000) {
    const k = count / 1000
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`
  }
  return count.toString()
}

export default function TranslatePage() {
  const router = useRouter();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { authToken, isStaff, username } = useContext(AuthContext);
  const { language } = useLanguage();
  const t = TRANSLATE[language] || TRANSLATE.en;
  // Single source of truth for tier-based gates — see
  // credits/tier_limits.py on the backend. Local isStaff (decoded from
  // JWT) is preserved as an early-return so staff aren't blocked while
  // the tier-limits fetch is in flight on cold page load.
  const tierLimits = useTierLimits();
  
  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [authLoading, isAuthenticated, router]);
  
  // State management
  const [userPlan, setUserPlan] = useState(null); // Start as null to detect loading state
  const [planLoading, setPlanLoading] = useState(true);
  const [maxCharsLimit, setMaxCharsLimit] = useState(5000); // Dynamic character limit based on plan
  const [autoSelectedEngine, setAutoSelectedEngine] = useState(null);
  
  // Initialize states with localStorage values if available
  const [inputMode, setInputMode] = useState(() => {
    if (typeof window !== 'undefined') {
      // Check URL params first for mode override from TTS page
      const urlParams = new URLSearchParams(window.location.search);
      const modeFromUrl = urlParams.get('mode');

      if (modeFromUrl) {
        return modeFromUrl;
      }
      // Fall back to localStorage if no query param
      return localStorage.getItem('translate_inputMode') || 'text';
    }
    return 'text';
  });
  
  const [inputText, setInputText] = useState(() => {
    if (typeof window !== 'undefined') {
      // Check query params first for text from TTS page
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('text')) {
        return urlParams.get('text');
      }
    }
    return '';
  });

  const [highlightedWords, setHighlightedWords] = useState([]);
  
  const [sourceLanguage, setSourceLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('translate_sourceLanguage') || '';
    }
    return '';
  });
  
  const [targetLanguage, setTargetLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('translate_targetLanguage') || 'ht';
    }
    return 'ht';
  });

  // Speech-to-Speech "Real-time playback" toggle. When on AND target is
  // Haitian Creole, the recording flow uses POST /v1/translate/speech/
  // (Gemini multimodal — single call for STT + translation) and vendor-name-ok: dev-only architectural comment
  // immediately streams translated Creole audio back via the Live Stream
  // TTS pipeline. Audio starts playing ~3-4 s after Stop Recording.
  const [realtimePlayback, setRealtimePlayback] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('translate_realtimePlayback') === '1';
  });

  // Layout view mode. 'stacked' (default) keeps the classic top-down
  // Step 1 → 2 → 3 → History flow. 'split' matches the TTS/STT pages:
  // Steps on the left, resizable right pane with History / Options /
  // Settings tabs. Persisted so returning users get their choice.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'stacked';
    // Default to split; only fall back to stacked when the user has
    // explicitly saved that choice.
    return localStorage.getItem('translate_viewMode') === 'stacked' ? 'stacked' : 'split';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('translate_viewMode', viewMode);
    }
  }, [viewMode]);

  // Percentage width of the left pane in split view. Clamped 20–80 by the
  // drag handler. Matches STTLayoutEnhanced's mechanic exactly.
  const [dividerPosition, setDividerPosition] = useState(() => {
    if (typeof window === 'undefined') return 60;
    const raw = parseFloat(localStorage.getItem('translate_dividerPosition') || '60');
    return Number.isFinite(raw) && raw >= 20 && raw <= 80 ? raw : 60;
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('translate_dividerPosition', String(dividerPosition));
    }
  }, [dividerPosition]);

  // Which tab is active in the right pane while in split view.
  const [activeRightTab, setActiveRightTab] = useState(() => {
    if (typeof window === 'undefined') return 'history';
    const saved = localStorage.getItem('translate_rightTab');
    return saved === 'options' || saved === 'settings' ? saved : 'history';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('translate_rightTab', activeRightTab);
    }
  }, [activeRightTab]);

  const splitContainerRef = useRef(null);
  const isDraggingSplit = useRef(false);
  const handleSplitMouseMove = useCallback((e) => {
    if (!isDraggingSplit.current || !splitContainerRef.current) return;
    const rect = splitContainerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    if (pct >= 20 && pct <= 80) setDividerPosition(pct);
  }, []);
  const handleSplitMouseUp = useCallback(() => {
    isDraggingSplit.current = false;
    document.removeEventListener('mousemove', handleSplitMouseMove);
    document.removeEventListener('mouseup', handleSplitMouseUp);
  }, [handleSplitMouseMove]);
  const handleSplitMouseDown = (e) => {
    isDraggingSplit.current = true;
    document.addEventListener('mousemove', handleSplitMouseMove);
    document.addEventListener('mouseup', handleSplitMouseUp);
    e.preventDefault();
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('translate_realtimePlayback', realtimePlayback ? '1' : '0');
    }
  }, [realtimePlayback]);
  const liveStream = useLiveStream({
    onError: (err) => console.warn('[Translate S2S] Live Stream error:', err.message),
  });
  // Live-updating translation preview shown during mic recording when
  // Real-time playback is on. Populated every ~4 s by MicrophoneRecorder's
  // background-translation callback. Cleared when the user starts a fresh
  // recording or leaves the page.
  const [interimSpeechText, setInterimSpeechText] = useState('');
  
  // Translation quality is a two-option choice: Standard (Gemini 2.5, all tiers) vendor-name-ok: dev-only architectural comment
  // and Advanced (Gemini 3.5, Creator+). Non-eligible users see Advanced locked vendor-name-ok: dev-only architectural comment
  // as an upsell. Default to the best engine the plan allows.
  const STANDARD_ENGINE = 'gemini_gemini-2.5-flash';
  const ADVANCED_ENGINE = 'gemini_gemini-3.5-flash';
  const engineTouchedRef = useRef(false);
  const [translationEngineState, setTranslationEngineState] = useState(STANDARD_ENGINE);

  const setTranslationEngine = (value) => {
    engineTouchedRef.current = true;
    const valid = ['auto', STANDARD_ENGINE, ADVANCED_ENGINE];
    setTranslationEngineState(valid.includes(value) ? value : STANDARD_ENGINE);
  };

  const translationEngine = translationEngineState;

  // Default to Advanced for eligible plans (until the user picks), and never
  // leave a non-eligible user sitting on Advanced. Eligibility resolved
  // via the shared tier matrix (credits/tier_limits.py).
  useEffect(() => {
    if (!userPlan && !isStaff) return; // wait for plan to load
    const canAdvanced = isStaff || tierLimits.hasAdvancedEngines();
    if (!engineTouchedRef.current) {
      setTranslationEngineState(canAdvanced ? ADVANCED_ENGINE : STANDARD_ENGINE);
    } else if (!canAdvanced) {
      setTranslationEngineState(STANDARD_ENGINE);
    }
  }, [userPlan, isStaff, tierLimits]);
  
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationResult, setTranslationResult] = useState(null);
  const [translationJustCompleted, setTranslationJustCompleted] = useState(false);
  // "Did the user actually run Translate in THIS session?" — starts false
  // on every page load. Session-storage restores translationResult +
  // inputText, so a passive reload otherwise looks identical to "just
  // translated." This flag gates the Generate Speech flash so the button
  // only calls attention to itself right after the user's own Translate
  // click, not on cold loads.
  const [translateRanThisSession, setTranslateRanThisSession] = useState(false);
  const [error, setError] = useState(null);
  
  // Audio/transcription states for cost estimation
  const [audioFile, setAudioFile] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [transcriptionCost, setTranscriptionCost] = useState(null);

  // STT job state management (for async job flow)
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTranscriptionSuccess, setShowTranscriptionSuccess] = useState(false);
  const [currentSTTJobId, setCurrentSTTJobId] = useState(null);

  // Translation history state
  const [translationHistory, setTranslationHistory] = useState([]);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [selectedTranslationId, setSelectedTranslationId] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historyDateRange, setHistoryDateRange] = useState({ start: null, end: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [showHistoryMenus, setShowHistoryMenus] = useState({});
  const [showTranslatedText, setShowTranslatedText] = useState({}); // Track which text to show for each translation
  const [supportedLanguages, setSupportedLanguages] = useState({
    source_languages: [],
    target_languages: [],
    available_services: []
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Track if we've loaded from localStorage

  // Step 3 Show/Hide Options state
  const [showStep3Options, setShowStep3Options] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ut_showStep3Options');
      return saved !== null ? saved === 'true' : true;
    }
    return true;
  });

  // Audio recording settings
  const [selectedMicrophoneLabel, setSelectedMicrophoneLabel] = useState('Default Microphone');
  const [volumeBoost, setVolumeBoost] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('recordingGain');
      return saved ? parseFloat(saved) : 1.0;
    }
    return 1.0;
  });
  
  // Track if query params have been processed
  const [queryProcessed, setQueryProcessed] = useState(false);

  // Handle query parameters from TTS page redirect
  useEffect(() => {
    // Only process query params when router is ready and not yet processed
    if (!router.isReady || queryProcessed) return;

    // Mark as processed to prevent re-running
    setQueryProcessed(true);

    if (router.query.text) {
      setInputText(router.query.text);
    }
    if (router.query.mode) {
      setInputMode(router.query.mode);
    }
    if (router.query.targetLang) {
      setTargetLanguage(router.query.targetLang);
    }
    if (router.query.highlightWords) {
      try {
        const wordsToHighlight = JSON.parse(router.query.highlightWords);
        setHighlightedWords(wordsToHighlight);
      } catch (error) {
        console.error('[Translate] Error parsing highlight words:', error);
      }
    }

    // Check for scroll flag
    if (router.query.scrollToButton === 'true') {
      setShouldAutoScroll(true);

      // Remove the scroll parameter from URL to prevent re-scrolling on refresh
      const { scrollToButton, ...restQuery } = router.query;
      router.replace({
        pathname: router.pathname,
        query: restQuery
      }, undefined, { shallow: true });
    }
  }, [router.isReady, router.query, queryProcessed]); // Include queryProcessed to ensure stable dependencies
  
  // Track if we should scroll (only on initial transfer from TTS)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  // Track scroll position before history updates
  const scrollPositionRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  // Preserve scroll position when loading more history
  useLayoutEffect(() => {
    if (isLoadingMoreRef.current && !isLoadingHistory) {
      // History has finished loading, restore scroll position
      // Target the main scrollable container (the <main> element from Layout.js)
      const scrollContainer = document.querySelector('main.flex-1.overflow-y-auto') ||
                             document.querySelector('.flex-1.overflow-y-auto.dark\\:text-dark-text') ||
                             document.querySelector('.flex-1.overflow-y-auto');
      if (scrollContainer && scrollPositionRef.current > 0) {
        scrollContainer.scrollTop = scrollPositionRef.current;
        console.log('[Translation] Restored scroll via useLayoutEffect:', scrollPositionRef.current);
        console.log('[Translation] Container classes:', scrollContainer.className);
      }
      isLoadingMoreRef.current = false;
      scrollPositionRef.current = 0;
    }
  }, [isLoadingHistory, translationHistory]);

  // Auto-scroll to "Transcribe + Translate" button when navigating from TTS page
  // Separate effect for scrolling after state updates
  useEffect(() => {
    // Only scroll if flag is set
    if (!shouldAutoScroll) return;

    console.log('[Translate] *** AUTO-SCROLL ACTIVATED ***');

    let hasScrolled = false; // Track if we've already scrolled

    // Wait for DOM to be ready and components to render
    const scrollInterval = setInterval(() => {
      // If we've already scrolled, don't do it again
      if (hasScrolled) {
        return;
      }

      console.log('[Translate] Looking for translate button...');

      // Try to find the button
      const button = document.getElementById('translate-button');
      const container = document.getElementById('translate-button-container');

      // Skip if we're currently loading history (which might cause DOM changes)
      if (isLoadingHistory) {
        console.log('[Translate] Skipping scroll - history is loading');
        return;
      }

      // Also check if button exists by searching for the text
      const allButtons = document.querySelectorAll('button');
      let translateBtn = null;
      allButtons.forEach(btn => {
        if (btn.textContent && btn.textContent.includes('Transcribe + Translate')) {
          translateBtn = btn;
          console.log('[Translate] Found button by text content!');
        }
      });

      const element = button || container || translateBtn;

      if (element) {
        console.log('[Translate] *** ELEMENT FOUND, SCROLLING NOW ***');
        console.log('[Translate] Element:', element);
        console.log('[Translate] Element ID:', element.id);
        console.log('[Translate] Element class:', element.className);

        // Clear the interval
        clearInterval(scrollInterval);

        // Calculate scroll position to put button at bottom of viewport
        const rect = element.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const elementTop = currentScrollTop + rect.top;
        const elementBottom = elementTop + rect.height;
        const viewportHeight = window.innerHeight;

        // We want the bottom of the button to be near the bottom of the viewport
        // So we scroll to: element bottom position - viewport height + small padding
        const targetScroll = elementBottom - viewportHeight + 80; // 80px from bottom of viewport

        console.log('[Translate] Scroll calculation details:', {
          currentScrollTop,
          elementTop,
          elementBottom,
          viewportHeight,
          targetScroll,
          rectTop: rect.top,
          rectHeight: rect.height
        });

        // Mark that we've scrolled
        hasScrolled = true;

        // Try both methods for better compatibility
        // Method 1: Direct calculation
        if (targetScroll > 0) {
          window.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        } else {
          // Method 2: If calculation seems off, use scrollIntoView
          console.log('[Translate] Using scrollIntoView as fallback');
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'end',  // This should put it at the bottom
            inline: 'nearest'
          });
        }

        // Clear the flag
        setShouldAutoScroll(false);
      } else {
        console.log('[Translate] Button not found yet, will retry...');
      }
    }, 500);

    // Stop trying after 10 seconds
    setTimeout(() => {
      clearInterval(scrollInterval);
      if (shouldAutoScroll) {
        console.log('[Translate] *** GIVING UP - Could not find button after 10 seconds ***');
        setShouldAutoScroll(false);
      }
    }, 10000);

    return () => clearInterval(scrollInterval);
  }, [shouldAutoScroll, isLoadingHistory]); // Run when shouldAutoScroll changes or history loading state changes
  
  // TTS state
  const [showTTSOptions, setShowTTSOptions] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('translate_selectedVoice') || null;
    }
    return null;
  });
  const [voices, setVoices] = useState([]);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [ttsSuccess, setTtsSuccess] = useState(false);
  // Emotion-tags hard-block state — modal opens on Generate Speech
  // when the translated text has [laughs]/[whispers]-style tags AND
  // the picked voice can't interpret them (Regional/VITS). The vendor-name-ok: dev-only architectural comment
  // 2026-09-04 backend guard (bcc860a2) rejects these submissions
  // regardless, and the modal no longer offers a "Proceed anyway"
  // bypass; users can Cancel or Delete-tags-and-Generate.
  const [showEmotionTagsModal, setShowEmotionTagsModal] = useState(false);
  const [currentTtsJobId, setCurrentTtsJobId] = useState(null);
  const currentTtsJobIdRef = useRef(null); // Ref to immediately store job ID
  const [autoTriggeredTTS, setAutoTriggeredTTS] = useState(false); // Track if TTS was auto-triggered
  const autoTriggeredTTSRef = useRef(false); // Ref to avoid stale closure issues
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const [inlineAudioUrl, setInlineAudioUrl] = useState(null);
  
  // Download/Export state
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteEntryConfirm, setShowDeleteEntryConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [showInlinePlayer, setShowInlinePlayer] = useState(false);
  const [wordTimings, setWordTimings] = useState(null);
  const [ttsProgress, setTtsProgress] = useState({
    status: null, // 'queued', 'synthesizing', 'uploading', 'completed'
    queuePosition: null,
    message: ''
  });
  const [autoPlayAfterTranslation, setAutoPlayAfterTranslation] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('autoPlayAfterTranslation');
      return saved !== null ? saved === 'true' : true; // Default to true
    }
    return true;
  });
  const [isNewlyGeneratedAudio, setIsNewlyGeneratedAudio] = useState(false);
  
  // Unified audio player state
  const [currentlyPlayingAudio, setCurrentlyPlayingAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false); // Track actual playing state
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebarPinned') === 'true';
    }
    return false;
  });
  const sharedAudioPlayerRef = useRef(null);
  const audioPlayerContainerRef = useRef(null);
  const scrollStartPosRef = useRef(null); // Track scroll start position for threshold
  const [savedAudioPositions, setSavedAudioPositions] = useState({}); // Store saved playback positions per item_id

  const [isEngineCollapsed, setIsEngineCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('translationEngineCollapsed');
      // If there's a saved preference, use it; otherwise default to collapsed
      return saved !== null ? saved === 'true' : true;
    }
    return true; // Default to collapsed
  });
  
  // Track expanded rows directly in parent component for full control
  const [expandedHistoryRows, setExpandedHistoryRows] = useState(new Set());

  // When a new entry is added to the expanded set (usually by
  // expandTranslationInHistory after TTS delivered), smooth-scroll the
  // History card into view so the user's eye is drawn to the completed
  // entry. Only fires when the set GROWS — no scroll when the user
  // manually toggles a row closed.
  useEffect(() => {
    const size = expandedHistoryRows.size;
    if (size > lastExpandedCountRef.current) {
      const el = historyCardRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (_) { /* noop */ }
      }
    }
    lastExpandedCountRef.current = size;
  }, [expandedHistoryRows]);
  
  // Debug logging state - using ref to avoid re-renders
  const debugLogsRef = useRef([]);
  const translationResultRef = useRef(null);
  // Wrapper around the History card. Used to smooth-scroll it into view
  // when a new item is auto-expanded after TTS delivered — feels like a
  // guided next-step handoff to the user.
  const historyCardRef = useRef(null);
  const lastExpandedCountRef = useRef(0);
  
  // Function to expand a specific translation in history
  const expandTranslationInHistory = (translationId) => {
    if (translationId) {
      console.log('[Translation] Expanding history item:', translationId);
      setExpandedHistoryRows(prev => {
        const newSet = new Set(prev);
        newSet.add(translationId);
        return newSet;
      });
    }
  };
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showDebugButton, setShowDebugButton] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('showDebugButton') === 'true';
    }
    return false;
  });
  
  // Safe JSON stringify that handles circular references
  const safeStringify = (obj) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    }, 2);
  };
  
  // Debug logging function - logs to memory without causing re-renders
  const debugLog = useCallback((message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      message,
      data: data ? safeStringify(data) : null
    };
    
    // Store in ref to avoid re-renders
    debugLogsRef.current.push(logEntry);
    
    // Keep only last 1000 entries to prevent memory issues
    if (debugLogsRef.current.length > 1000) {
      debugLogsRef.current = debugLogsRef.current.slice(-1000);
    }
  }, []);
  
  // Function to download logs as a file
  const downloadLogs = useCallback(() => {
    const logs = debugLogsRef.current.map(entry => {
      return `[${entry.timestamp}] ${entry.message}${entry.data ? '\n' + entry.data : ''}`;
    }).join('\n\n');
    
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `translation-debug-logs-${new Date().toISOString().replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);
  
  // Translation engine options with plan-based restrictions (ordered by quality)
  // `quality` stays a stable English code (color-picker in
  // TranslationStep2Language switch-compares it); `qualityLabel` is
  // the localized display fallback used by that same component when
  // present.
  const engineOptions = {
    'auto': {
      name: t.eng_auto_name,
      description: t.eng_auto_desc,
      planRequired: 'free',
      planName: t.plan_free,
      icon: '🤖',
      quality: 'Variable',
      qualityLabel: t.qual_variable
    },
    'gemini_gemini-3.5-flash': {
      name: t.eng_advanced_name,
      description: t.eng_advanced_desc,
      planRequired: 'creator',
      planName: t.plan_creator,
      icon: '💠',
      quality: 'Best',
      qualityLabel: t.qual_best
    },
    'gemini_gemini-2.5-flash': {
      name: t.eng_standard_name,
      description: t.eng_standard_desc,
      planRequired: 'free',
      planName: t.plan_free,
      icon: '💎',
      quality: 'Very Good',
      qualityLabel: t.qual_very_good
    }
    // Hidden engines - kept for backward compatibility but not shown in UI
    // 'google': {
    //   name: 'Google Translate',
    //   description: 'High-quality neural translation',
    //   planRequired: 'learner',
    //   planName: 'Learner',
    //   icon: '🌐',
    //   quality: 'Good'
    // },
    // 'libretranslate': {
    //   name: 'LibreTranslate',
    //   description: 'Open-source translation',
    //   planRequired: 'free',
    //   planName: 'Free',
    //   icon: '🆓',
    //   quality: 'Basic'
    // }
  };

  // Access gates — resolved from the shared tier matrix
  // (credits/tier_limits.py). Local `isStaff` is preserved for the
  // in-flight-loading window; the hook's helpers include staff bypass
  // once the backend responds.
  const hasVoiceAccess = () => (isStaff ? true : tierLimits.hasVoiceAccess());
  const hasProAccess = () => (isStaff ? true : tierLimits.hasProAccess());

  // Check if user has access to a specific engine based on updated requirements
  const hasEngineAccess = (engineCode) => {
    if (isStaff) return true;
    
    const engine = engineOptions[engineCode];
    // If userPlan is still null (loading), default to free to show proper locks
    const plan = userPlan ? userPlan.toLowerCase() : 'free';
    debugLog(`Checking access for engine: ${engineCode}`, { engineCode, userPlan: plan });
    
    
    // Free plan: Auto-Select + Gemini 2.5 Flash vendor-name-ok: dev-only architectural comment
    if (plan === 'free') {
      return ['auto', 'gemini_gemini-2.5-flash'].includes(engineCode);
    }

    // Learner plan: Auto-Select + Gemini 2.5 Flash vendor-name-ok: dev-only architectural comment
    if (plan === 'learner') {
      return ['auto', 'gemini_gemini-2.5-flash'].includes(engineCode);
    }

    // Creator plan: Auto-Select, GPT-5 Mini, Claude Haiku 4.5, Gemini 2.5 + 3.5 Flash vendor-name-ok: dev-only architectural comment
    if (plan === 'creator') {
      return ['auto', 'openai_gpt-5-mini', 'anthropic_claude-haiku-4-5', 'gemini_gemini-2.5-flash', 'gemini_gemini-3.5-flash'].includes(engineCode);
    }
    
    // Pro+ plans: All engines including premium ones. Resolved via
    // the tier matrix so a plan-type addition (e.g. a future 'team'
    // plan) that sets advanced_engines=true automatically inherits.
    if (tierLimits.hasAdvancedEngines()) {
      return true;
    }
    
    return false;
  };

  // Determine which engine would be auto-selected based on plan
  const getAutoSelectedEngine = () => {
    if (!userPlan) return null;

    const plan = typeof userPlan === 'string' ? userPlan.toLowerCase() : 'free';

    // Define the priority order for each plan (matches backend translation_service.py)
    // Backend tries engines in order until one works - GPT-4 is highest priority
    // Gemini-first by tier (mirrors backend): below Creator → 2.5 "Standard", vendor-name-ok: dev-only architectural comment
    // Creator+ → 3.5 "Advanced". Other engines remain only as silent fallbacks.
    const planHierarchy = {
      'free': ['gemini_gemini-2.5-flash'],
      'learner': ['gemini_gemini-2.5-flash'],
      'creator': ['gemini_gemini-3.5-flash', 'gemini_gemini-2.5-flash'],
      'pro': ['gemini_gemini-3.5-flash', 'gemini_gemini-2.5-flash'],
      'business': ['gemini_gemini-3.5-flash', 'gemini_gemini-2.5-flash'],
      'enterprise': ['gemini_gemini-3.5-flash', 'gemini_gemini-2.5-flash'],
      'staff': ['gemini_gemini-3.5-flash', 'gemini_gemini-2.5-flash']
    };

    const availableEngines = planHierarchy[plan] || planHierarchy['free'];

    // The backend tries engines in order, but for display we'll show the first (best) one
    return availableEngines[0];
  };

  // Log component mount
  useEffect(() => {
    debugLog('Translation page mounted', { isStaff, username });
  }, []);
  
  // Save user preferences to localStorage when they change
  useEffect(() => {
    if (typeof window !== 'undefined' && inputMode) {
      localStorage.setItem('translate_inputMode', inputMode);
    }
  }, [inputMode]);

  useEffect(() => {
    if (typeof window !== 'undefined' && sourceLanguage !== undefined) {
      localStorage.setItem('translate_sourceLanguage', sourceLanguage);
    }
  }, [sourceLanguage]);

  // Save showStep3Options to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('ut_showStep3Options', showStep3Options.toString());
    }
  }, [showStep3Options]);

  // Listen for microphone and volume changes from localStorage (for sync with MicrophoneRecorder)
  useEffect(() => {
    const updateMicrophoneLabel = async () => {
      const savedDevice = localStorage.getItem('preferredMicrophone');

      if (savedDevice === 'default' || !savedDevice) {
        setSelectedMicrophoneLabel('Default Microphone');
      } else {
        // Only request microphone permission if voice input mode is selected
        if (inputMode === 'voice') {
          try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');
            const selectedDevice = audioInputs.find(device => device.deviceId === savedDevice);

            if (selectedDevice) {
              setSelectedMicrophoneLabel(selectedDevice.label || `Microphone ${savedDevice.substring(0, 8)}`);
            } else {
              setSelectedMicrophoneLabel('Default Microphone');
            }
          } catch (err) {
            console.error('Error accessing microphone:', err);
            setSelectedMicrophoneLabel('Default Microphone');
          }
        } else {
          setSelectedMicrophoneLabel('Default Microphone');
        }
      }
    };

    const updateVolumeBoost = () => {
      const savedGain = localStorage.getItem('recordingGain');
      if (savedGain) {
        setVolumeBoost(parseFloat(savedGain));
      }
    };

    // Initial update
    updateMicrophoneLabel();
    updateVolumeBoost();

    // Listen for storage events (changes from other components)
    const handleStorageChange = (e) => {
      if (e.key === 'preferredMicrophone') {
        updateMicrophoneLabel();
      } else if (e.key === 'recordingGain') {
        updateVolumeBoost();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (for same-tab updates)
    const handleMicrophoneChange = () => updateMicrophoneLabel();
    const handleVolumeChange = () => updateVolumeBoost();

    window.addEventListener('microphoneChanged', handleMicrophoneChange);
    window.addEventListener('volumeChanged', handleVolumeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('microphoneChanged', handleMicrophoneChange);
      window.removeEventListener('volumeChanged', handleVolumeChange);
    };
  }, [inputMode]);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && targetLanguage) {
      localStorage.setItem('translate_targetLanguage', targetLanguage);
    }
  }, [targetLanguage]);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && translationEngine) {
      localStorage.setItem('translate_translationEngine', translationEngine);
    }
  }, [translationEngine]);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (selectedVoice) {
        localStorage.setItem('translate_selectedVoice', selectedVoice);
      } else {
        localStorage.removeItem('translate_selectedVoice');
      }
    }
  }, [selectedVoice]);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('translate_autoPlay', autoPlayAfterTranslation ? 'true' : 'false');
    }
  }, [autoPlayAfterTranslation]);
  
  // Load saved data from localStorage after component mounts
  useEffect(() => {
    if (!isDataLoaded && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('translationFormData');
        if (saved) {
          const data = JSON.parse(saved);
          
          // Apply saved data to state ONLY if we don't have query parameters
          // Don't overwrite text that came from query params (e.g., from TTS page)
          if (data.inputText && !router.query.text) setInputText(data.inputText);
          
          // Skip loading language settings from translationFormData
          // as we now use dedicated localStorage items for these
          // if (data.sourceLanguage !== undefined) setSourceLanguage(data.sourceLanguage);
          // if (data.targetLanguage) setTargetLanguage(data.targetLanguage);
          // if (data.translationEngine) setTranslationEngine(data.translationEngine);
          if (data.translationResult) setTranslationResult(data.translationResult);
          if (data.showTTSOptions !== undefined) setShowTTSOptions(data.showTTSOptions);
          if (data.selectedVoice) setSelectedVoice(data.selectedVoice);
          if (data.selectedTranslationId) setSelectedTranslationId(data.selectedTranslationId);
          if (data.inlineAudioUrl) setInlineAudioUrl(data.inlineAudioUrl);
          if (data.showInlinePlayer !== undefined) setShowInlinePlayer(data.showInlinePlayer);
          if (data.wordTimings) setWordTimings(data.wordTimings);
        }
      } catch (e) {
        console.error('[Translation] Failed to load saved form data:', e);
      }
      setIsDataLoaded(true);
    }
  }, [isDataLoaded]);

  // Auto-save form data to localStorage
  useEffect(() => {
    if (isDataLoaded) {
      const formData = {
        inputText,
        // Don't save language settings here - they're saved individually now
        // sourceLanguage,
        // targetLanguage,
        // translationEngine,
        translationResult,
        showTTSOptions,
        selectedVoice,
        selectedTranslationId,
        inlineAudioUrl,
        showInlinePlayer,
        wordTimings,
        timestamp: new Date().toISOString()
      };
      
      try {
        localStorage.setItem('translationFormData', safeStringify(formData));
      } catch (e) {
        console.error('[Translation] Failed to save form data:', e);
      }
    }
  }, [inputText, sourceLanguage, targetLanguage, translationEngine, translationResult, showTTSOptions, selectedVoice, selectedTranslationId, inlineAudioUrl, showInlinePlayer, wordTimings, isDataLoaded]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, authLoading, router]);

  // Log when user plan changes
  useEffect(() => {
    if (userPlan !== null) {
      debugLog('User plan changed', {
        userPlan,
        availableEngines: Object.keys(engineOptions).filter(code => hasEngineAccess(code))
      });
    }
  }, [userPlan]);

  // Set auto-selected engine when user plan is loaded and translation engine is 'auto'
  useEffect(() => {
    if (userPlan && translationEngine === 'auto') {
      const selectedEngine = getAutoSelectedEngine();
      debugLog('Auto-setting engine based on plan:', userPlan, '-> Engine:', selectedEngine);
      setAutoSelectedEngine(selectedEngine);
    } else if (translationEngine !== 'auto') {
      setAutoSelectedEngine(null);
    }
  }, [userPlan, translationEngine]);
  
  // Fetch initial data
  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchSupportedLanguages();
      if (window.refreshPlanCredits) window.refreshPlanCredits();
      fetchVoices();
      fetchUserPlan();
      // Don't fetch history here - let the search/date useEffects handle it
    }
  }, [isAuthenticated, authToken]);
  
  // Fetch full translation details when selected
  const fetchTranslationDetails = async (translationId) => {
    setIsLoadingDetails(true);
    setError(null);
    
    try {
      debugLog('Fetching full translation details', { translationId });
      const response = await api.get(`/v1/translate/history/${translationId}/`);
      
      if (response.data) {
        debugLog('Translation details fetched', {
          hasTranslatedText: !!response.data.translated_text,
          hasTtsJob: !!response.data.tts_job,
          hasTtsJobDetails: !!response.data.tts_job_details,
          audioUrl: response.data.tts_job_details?.audio_url
        });
        
        // Update the translation result with full details
        setTranslationResult(response.data);
        translationResultRef.current = response.data;
        
        // Populate input fields with the selected translation
        setInputText(response.data.original_text || '');
        setSourceLanguage(response.data.source_language || '');
        setTargetLanguage(response.data.target_language || 'ht');
        if (response.data.service_used) {
          // Map backend service names to frontend engine codes
          const serviceToEngine = {
            'openai': 'openai_gpt-5.1',  // Default bare OpenAI to current flagship
            'anthropic': 'anthropic_claude-sonnet-4-6',  // Default bare Anthropic to current flagship
            'google': 'google',
            'libretranslate': 'libretranslate',
            'auto': 'auto'
          };
          
          const mappedEngine = serviceToEngine[response.data.service_used] || response.data.service_used;
          setTranslationEngine(mappedEngine);
        }
        
        // If translation has TTS, show TTS options
        if (response.data.target_language === 'ht') {
          setShowTTSOptions(true);
          
          // Clear any inline player state if we have saved audio
          if (response.data.has_tts && response.data.tts_job_details?.audio_url) {
            setShowInlinePlayer(false);
            setInlineAudioUrl(null);
            setIsSynthesizing(false); // Clear synthesizing state since audio already exists
            setTtsProgress({ status: null, queuePosition: null, message: '' });
            setIsNewlyGeneratedAudio(false); // This is historical audio
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch translation details:', err);
      setError(t.err_load_translation);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const fetchTranslationHistory = useCallback(async (loadMore = false, searchQuery = null, dateRange = null) => {
    if (!authToken) return;

    // Use passed params or current state
    const currentSearchQuery = searchQuery !== null ? searchQuery : historySearchQuery;
    const currentDateRange = dateRange !== null ? dateRange : historyDateRange;

    // Save scroll position before loading more using refs
    if (loadMore && typeof window !== 'undefined') {
      // Target the main scrollable container (the <main> element from Layout.js)
      const scrollContainer = document.querySelector('main.flex-1.overflow-y-auto') ||
                            document.querySelector('.flex-1.overflow-y-auto.dark\\:text-dark-text') ||
                            document.querySelector('.flex-1.overflow-y-auto');
      if (scrollContainer) {
        scrollPositionRef.current = scrollContainer.scrollTop;
        isLoadingMoreRef.current = true;
        console.log('[Translation] Saved scroll position to ref:', scrollPositionRef.current);
        console.log('[Translation] Container classes:', scrollContainer.className);
      }
    }

    setIsLoadingHistory(true);
    try {
      const offset = loadMore ? historyOffset : 0;
      const pageSize = 10;
      const params = {
        limit: pageSize,
        offset: offset
      };
      
      // Add search query if present
      if (currentSearchQuery.trim()) {
        params.search = currentSearchQuery.trim();
        console.log('[Translation History] Adding search param:', params.search);
      }
      
      // Add date range filters if present
      if (currentDateRange.start) {
        params.start_date = currentDateRange.start.toISOString().split('T')[0];
      }
      if (currentDateRange.end) {
        params.end_date = currentDateRange.end.toISOString().split('T')[0];
      }
      
      console.log('[Translation History] Fetching with params:', params);
      debugLog('Fetching translation history', { params, loadMore });
      
      const response = await api.get('/v1/translate/history/', { params });
      
      console.log('[Translation History] Response:', {
        count: response.data.count,
        resultsLength: response.data.results.length,
        searchQuery: params.search,
        firstResults: response.data.results.slice(0, 3).map(r => ({
          original: r.original_text.substring(0, 50),
          translated: r.translated_text.substring(0, 50)
        }))
      });
      
      // Filter out hidden translations
      const visibleHistory = response.data.results.filter(item => !item.is_hidden);
      
      // Sort function to put favorites first
      const sortWithFavoritesFirst = (items) => {
        return [...items].sort((a, b) => {
          // If both are favorites or both are not, maintain original order
          if (a.is_favorite === b.is_favorite) {
            return 0;
          }
          // Favorites come first
          return a.is_favorite ? -1 : 1;
        });
      };
      
      if (loadMore) {
        setTranslationHistory(prev => sortWithFavoritesFirst([...prev, ...visibleHistory]));
      } else {
        setTranslationHistory(sortWithFavoritesFirst(visibleHistory));
      }
      
      setHistoryHasMore(response.data.has_more);
      setHistoryOffset(offset + visibleHistory.length);
      
      // Update pagination info
      setHistoryTotalCount(response.data.count || 0);
      const currentPage = Math.floor(offset / pageSize) + 1;
      setHistoryCurrentPage(currentPage);

      // Scroll restoration is now handled by useLayoutEffect
    } catch (err) {
      console.error('Failed to fetch translation history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [authToken, historyOffset, historySearchQuery, historyDateRange.start, historyDateRange.end]);

  // Handle selecting a translation from history
  useEffect(() => {
    if (selectedTranslationId && translationHistory.length > 0) {
      // Check if we already have the full details (from a new translation)
      if (translationResult && translationResult.id === selectedTranslationId) {
        debugLog('Translation details already loaded', { 
          translationId: selectedTranslationId,
          hasAudio: !!translationResult.tts_job_details?.audio_url 
        });
        return; // Skip fetching if we already have the data
      }
      
      // Fetch full details for the selected translation
      fetchTranslationDetails(selectedTranslationId);
    }
  }, [selectedTranslationId, translationHistory.length]);
  
  // Load selected translation details on page load (after history is loaded)
  useEffect(() => {
    if (isDataLoaded && selectedTranslationId && translationHistory.length > 0 && !translationResult) {
      debugLog('Loading selected translation on page load', { selectedTranslationId });
      fetchTranslationDetails(selectedTranslationId);
    }
  }, [isDataLoaded, selectedTranslationId, translationHistory.length]);

  // Combined effect for search and date range changes
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    
    // Create a stable reference to track if this is the first load
    const isFirstLoad = historySearchQuery === '' && !historyDateRange.start && !historyDateRange.end;
    
    const timeoutId = setTimeout(() => {
      console.log('[History Fetch] Triggered by:', {
        searchQuery: historySearchQuery,
        dateRange: historyDateRange,
        isFirstLoad
      });
      
      // Reset offset and page when filters change
      setHistoryOffset(0);
      setHistoryCurrentPage(1);
      fetchTranslationHistory(false, historySearchQuery, historyDateRange);
    }, isFirstLoad ? 0 : 300); // Debounce except on first load
    
    return () => clearTimeout(timeoutId);
  }, [historySearchQuery, historyDateRange.start, historyDateRange.end, isAuthenticated, authToken]);

  // Close history menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.keys(showHistoryMenus).length > 0) {
        setShowHistoryMenus({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showHistoryMenus]);

  // Track if we should auto-play when the player opens
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);


  // Unified audio player handlers (defined before WebSocket handler to be accessible)
  const playAudio = useCallback((audioData) => {
    // Save position of currently playing audio before switching to a new one
    if (currentlyPlayingAudio?.item_id && sharedAudioPlayerRef.current && isPlaying) {
      const currentTime = sharedAudioPlayerRef.current.currentTime || 0;
      console.log(`[Translation playAudio] Saving position for previous audio ${currentlyPlayingAudio.item_id}:`, currentTime);
      setSavedAudioPositions(prev => ({
        ...prev,
        [currentlyPlayingAudio.item_id]: currentTime
      }));
    }

    // Always set the audio data - this allows rows to takeover the player
    // Even if it's the same URL, we want to update metadata and ensure playback
    setCurrentlyPlayingAudio({
      audio_url: audioData.audio_url,
      word_timings: audioData.word_timings || wordTimings || null,
      translated_text: audioData.translated_text || translationResult?.translated_text || '',
      title: audioData.title || 'Translation Audio',
      item_id: audioData.item_id || null,
      metadata: audioData.metadata || null  // Preserve the metadata if provided
    });
    
    // Set flag to auto-play when player opens or when switching to new audio
    setShouldAutoPlay(true);
    
    // Set word timings for highlighting if available
    if (audioData.word_timings) {
      setWordTimings(audioData.word_timings);
      setShowInlinePlayer(true);
    }
    
    // The AudioPlayerWithDuration component handles autoPlay internally
    // No need to manually trigger play() as it might interfere with volume settings
  }, [translationResult, wordTimings, currentlyPlayingAudio, inputMode, isPlaying]);

  const stopAudio = useCallback(() => {
    // Let the AudioPlayerWithDuration component handle pause by setting audio to null
    setCurrentlyPlayingAudio(null);
    setHighlightedWordIndex(-1);
    setIsPlaying(false);
  }, []);

  const pauseAudio = useCallback(() => {
    // Save position before pausing
    if (currentlyPlayingAudio?.item_id && sharedAudioPlayerRef.current) {
      const currentTime = sharedAudioPlayerRef.current.currentTime || 0;
      console.log(`[Translation pauseAudio] Saving audio position for item ${currentlyPlayingAudio.item_id}:`, currentTime);
      setSavedAudioPositions(prev => ({
        ...prev,
        [currentlyPlayingAudio.item_id]: currentTime
      }));
    }

    // Pause without closing the player - just trigger pause on the audio element
    if (sharedAudioPlayerRef.current) {
      sharedAudioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  }, [currentlyPlayingAudio]);

  const resumeAudio = useCallback(() => {
    // Resume playback without resetting
    if (sharedAudioPlayerRef.current) {
      sharedAudioPlayerRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  // WebSocket handler for TTS updates
  const handleWebSocketMessage = useCallback((data) => {
    console.log('[Translation] WebSocket message:', data.type, data.job_id, data);
    debugLog('WebSocket message received', {
      type: data.type,
      jobId: data.job_id,
      currentTtsJobId,
      isMyJob: data.job_id === currentTtsJobId,
      translationId: data.translation_id,
      selectedTranslationId,
      data: data
    });
    
    // Handle TTS completion for any job (to update history icons)
    if (data.type === 'tts_job_delivered' && data.job_id) {
      debugLog('TTS job delivered - updating history', { 
        jobId: data.job_id,
        translationId: data.translation_id,
        selectedTranslationId,
        currentTtsJobId
      });
      
      // Update the history immediately to show the speech icon
      setTranslationHistory(prev => prev.map(item => {
        // Check if this translation's TTS job just completed
        // Match by translation_id from the WebSocket data OR by checking if this is the current job
        if ((data.translation_id && item.id === data.translation_id) ||
            (item.id === selectedTranslationId && String(currentTtsJobId) === String(data.job_id))) {
          console.log(`[WebSocket] Updating translation ${item.id} with has_tts=true`);
          return { ...item, has_tts: true, tts_job: data.job_id };
        }
        return item;
      }));
      
      // Expand the translation in history if it matches the translation_id from the WebSocket
      if (data.translation_id) {
        console.log('[Translation] Expanding translation from WebSocket translation_id:', data.translation_id);
        expandTranslationInHistory(data.translation_id);
      } else if (selectedTranslationId && String(currentTtsJobId) === String(data.job_id)) {
        // If no translation_id in the message, but the job ID matches our current job, expand the selected translation
        console.log('[Translation] Expanding selected translation as job ID matches:', selectedTranslationId);
        expandTranslationInHistory(selectedTranslationId);
      }
      
      // Refresh history to get the latest data after a short delay
      setTimeout(() => {
        fetchTranslationHistory(false, historySearchQuery, historyDateRange);
      }, 1000);
      
      // Also check if this completed job is for the current selected translation
      // This handles jobs that were started on the TTS page
      if (selectedTranslationId && data.translation_id) {
        // If the completed job is for the currently selected translation, refresh its details
        if (data.translation_id === selectedTranslationId) {
          debugLog('Completed job is for the selected translation - refreshing details', {
            selectedTranslationId,
            jobId: data.job_id,
            translationId: data.translation_id
          });
          
          // Refresh the selected translation details to get the audio
          fetchTranslationDetails(selectedTranslationId);
        }
      }
      
      // Don't clear autoTriggeredTTSRef here - it needs to be available for the main handler below
    }
    
    // Debug the comparison
    console.log('[WebSocket] Job ID comparison:', {
      dataJobId: data.job_id,
      dataJobIdType: typeof data.job_id,
      currentTtsJobId: currentTtsJobId,
      currentTtsJobIdType: typeof currentTtsJobId,
      areEqual: data.job_id === currentTtsJobId,
      strictEqual: data.job_id === currentTtsJobId,
      looseEqual: data.job_id == currentTtsJobId,
      stringComparison: String(data.job_id) === String(currentTtsJobId)
    });
    
    // Compare job IDs as strings to handle any type mismatches OR check if this is an auto-triggered job
    // Check both the ref and state to handle race conditions
    // ALSO accept webhooks if we're synthesizing but don't have a job ID yet (race condition: webhook arrives before API response)
    const isMatchingJob = data.job_id && (
      autoTriggeredTTSRef.current ||
      autoTriggeredTTS ||
      (currentTtsJobIdRef.current && String(data.job_id) === String(currentTtsJobIdRef.current)) ||
      (currentTtsJobId && String(data.job_id) === String(currentTtsJobId)) ||
      (isSynthesizing && !currentTtsJobId && !currentTtsJobIdRef.current) // Accept early webhooks
    );

    if (isMatchingJob) {
      // If we don't have a job ID yet but received a webhook, capture it from the webhook
      if (!currentTtsJobIdRef.current && data.job_id) {
        console.log('[Translation] Capturing job ID from early webhook:', data.job_id);
        const jobId = String(data.job_id);
        currentTtsJobIdRef.current = jobId;
        setCurrentTtsJobId(jobId);

        // Subscribe to this job immediately to ensure we get future webhooks
        if (subscribeToJob) {
          console.log('[Translation] Subscribing to job from early webhook:', jobId);
          subscribeToJob(jobId);
        }
      }
      // Handle different job status updates
      if (data.type === 'tts_job_update') {
        // For long text, clear the progress UI when we get the first webhook response
        // This indicates the job is being processed on the backend
        if (translationResult && translationResult.translated_text.length >= AUTOPLAY_MAX_CHAR_LENGTH) {
          debugLog('Long text TTS job update received - clearing progress UI', {
            jobId: data.job_id,
            status: data.status
          });
          
          // Clear the spinner and "Go to TTS" prompt
          setIsSynthesizing(false);
          setTtsProgress({
            status: null,
            queuePosition: null,
            message: ''
          });
          
          // Clear timeouts
          if (window.ttsLongTextTimeoutId) {
            clearTimeout(window.ttsLongTextTimeoutId);
            window.ttsLongTextTimeoutId = null;
          }
          
          // Unsubscribe since the job is being handled on TTS page
          if (unsubscribeFromJob) {
            unsubscribeFromJob(data.job_id);
          }
          
          return; // Don't process further for long text
        }
        
        // For short text, update progress normally
        if (data.status === 'queued') {
          setTtsProgress({
            status: 'queued',
            queuePosition: data.queue_position,
            message: data.queue_position ? `Position ${data.queue_position} in queue` : 'Queued for processing'
          });
        } else if (data.status === 'processing') {
          setTtsProgress({
            status: 'synthesizing',
            queuePosition: null,
            message: 'Synthesizing speech...'
          });
        } else if (data.status === 'completed' && !data.audio_url) {
          // Synthesis done but uploading to storage
          setTtsProgress({
            status: 'uploading',
            queuePosition: null,
            message: 'Uploading audio...'
          });
        }
      }
      
      // Handle completion - check multiple event types (separate if, not else if)
      if (data.type === 'tts_job_delivered') {
        // Check for audio URL in various fields
        const audioUrl = data.audio_url || data.audio_file_url;
        debugLog('TTS job delivered', {
          audioUrl,
          hasWordTimings: !!data.word_timings,
          autoPlayEnabled: autoPlayAfterTranslation,
          isSynthesizing,
          data: data
        });
        
        if (audioUrl) {
          console.log('[Translation] TTS completed, setting audio URL:', audioUrl);
          console.log('[Translation] Auto-play enabled:', autoPlayAfterTranslation);
          console.log('[Translation] Is synthesizing:', isSynthesizing);
          console.log('[Translation] Auto-triggered:', autoTriggeredTTS);
          console.log('[Translation] Current TTS Job ID:', currentTtsJobId);
          setInlineAudioUrl(audioUrl);
          setShowInlinePlayer(true);
          setIsNewlyGeneratedAudio(true); // This is newly generated audio
          
          // Always set up the audio with metadata
          const voiceName = voices?.find(v => v.voice_id === selectedVoice)?.name || selectedVoice || '';
          const audioData = {
            audio_url: audioUrl,
            word_timings: data.word_timings || wordTimings,
            translated_text: translationResult?.translated_text,
            metadata: {
              date: new Date().toLocaleDateString(),
              time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
              source_language: sourceLanguage,
              target_language: targetLanguage,
              char_count: translationResult?.translated_text?.length || 0,
              duration: data.duration_formatted,
              voice_name: voiceName
            }
          };
          
          // Play audio if auto-play is enabled AND (auto-triggered OR job ID matches)
          // The autoTriggeredTTSRef check handles the case where job completes before currentTtsJobId is set
          if (autoPlayAfterTranslation && (autoTriggeredTTSRef.current || autoTriggeredTTS || isSynthesizing || String(currentTtsJobId) === String(data.job_id))) {
            console.log('[Translation] Auto-playing generated audio (autoTriggered:', autoTriggeredTTS, ', autoTriggeredRef:', autoTriggeredTTSRef.current, ', isSynthesizing:', isSynthesizing, ')');
            // Clear the auto-triggered flag after using it
            setAutoTriggeredTTS(false);
            autoTriggeredTTSRef.current = false;
            // Delay playing audio until after success animation completes (10 seconds)
            // This prevents the unified player from appearing during the success animation
            setTimeout(() => {
              playAudio(audioData);
            }, 10000); // Wait for success animation to complete
          } else {
            console.log('[Translation] Auto-play disabled or not auto-triggered, setting up audio for manual play');
            // Even if not auto-playing, set up the audio data for when user clicks play
            // Also delay this to appear after success animation
            setTimeout(() => {
              setCurrentlyPlayingAudio({
                ...audioData,
                title: 'Translation Audio',
                item_id: null
              });
            }, 10000); // Wait for success animation to complete
          }
          
          // Clear the timeout since job completed
          if (window.ttsTimeoutId) {
            clearTimeout(window.ttsTimeoutId);
            window.ttsTimeoutId = null;
          }
          // Clear long text timeout as well
          if (window.ttsLongTextTimeoutId) {
            clearTimeout(window.ttsLongTextTimeoutId);
            window.ttsLongTextTimeoutId = null;
          }
          
          // Clear synthesizing state and update progress
          setIsSynthesizing(false);
          setTtsProgress({
            status: 'completed',
            queuePosition: null,
            message: 'Speech ready!'
          });
          
          // Set success state for animation
          setTtsSuccess(true);
          setTimeout(() => setTtsSuccess(false), 10000);
          // The "your next click is Generate Speech" hint is done — if
          // audioUrl ever gets cleared (retry, new session), Generate
          // Speech shouldn't flash again until the user re-runs Translate.
          setTranslateRanThisSession(false);
          
          // If this was an auto-triggered job, ensure we have the job ID set
          if (!currentTtsJobId && autoTriggeredTTS) {
            console.log('[Translation] Setting currentTtsJobId from auto-triggered delivered job:', data.job_id);
            const jobId = String(data.job_id);
            setCurrentTtsJobId(jobId);
            currentTtsJobIdRef.current = jobId;
          }
          
          // Clear progress after 5 seconds
          setTimeout(() => {
            setTtsProgress({
              status: null,
              queuePosition: null,
              message: ''
            });
          }, 5000);
          
          // Check for word timings
          if (data.word_timings) {
            console.log('[Translation] Received word timings:', data.word_timings);
            setWordTimings(data.word_timings);
          }
          
          // Update the history item immediately to show audio icon
          setTranslationHistory(prev => prev.map(item => 
            item.id === selectedTranslationId 
              ? { ...item, has_tts: true, tts_job: data.job_id }
              : item
          ));
          
          // Expand the translation in history immediately
          expandTranslationInHistory(selectedTranslationId);
          
          if (selectedTranslationId) {
            // Also refresh the selected translation details to get the audio
            fetchTranslationDetails(selectedTranslationId);
          }
          
          // Refresh translation history. Pass filters explicitly — the
          // useCallback closes over historySearchQuery/historyDateRange and
          // without this, a user with an active filter or date range would
          // see the just-completed row drop out of view on refetch.
          fetchTranslationHistory(false, historySearchQuery, historyDateRange);
          
          // Unsubscribe from this job
          if (unsubscribeFromJob) {
            unsubscribeFromJob(data.job_id);
          }
        } else {
          console.log('[Translation] TTS completed but no audio URL yet - waiting for S3 upload', data);
          debugLog('TTS completed without audio URL - waiting for S3', {
            jobId: data.job_id,
            allDataKeys: Object.keys(data),
            s3Status: data.s3_upload_status
          });
          // Update progress to show uploading state
          setTtsProgress({
            status: 'uploading',
            queuePosition: null,
            message: 'Uploading audio...'
          });
          // Don't clear synthesizing state yet - wait for S3 upload
          // Don't unsubscribe - we need to wait for s3_upload_complete event
        }
      } else if (data.type === 's3_upload_complete' && data.audio_url) {
        // Also update history for S3 upload completion
        if (data.job_id) {
          setTranslationHistory(prev => prev.map(item => {
            if (item.id === selectedTranslationId && String(currentTtsJobId) === String(data.job_id)) {
              return { ...item, has_tts: true, tts_job: data.job_id };
            }
            return item;
          }));
          
          // Check if this S3 upload is for the selected translation
          if (selectedTranslationId && data.translation_id === selectedTranslationId) {
            debugLog('S3 upload complete for selected translation - refreshing details', {
              selectedTranslationId,
              jobId: data.job_id,
              translationId: data.translation_id
            });
            fetchTranslationDetails(selectedTranslationId);
          }
        }
        debugLog('S3 upload complete', {
          audioUrl: data.audio_url,
          hasWordTimings: !!data.word_timings,
          data: data
        });
        
        console.log('[Translation] S3 upload completed, setting audio URL:', data.audio_url);
        setInlineAudioUrl(data.audio_url);
        
        // Set up the audio with metadata for the unified player
        const voiceName = voices?.find(v => v.voice_id === selectedVoice)?.name || selectedVoice || '';
        setCurrentlyPlayingAudio({
          audio_url: data.audio_url,
          word_timings: data.word_timings || wordTimings || null,
          translated_text: translationResult?.translated_text || '',
          title: 'Translation Audio',
          item_id: null,
          metadata: {
            date: new Date().toLocaleDateString(),
            time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            source_language: sourceLanguage,
            target_language: targetLanguage,
            char_count: translationResult?.translated_text?.length || 0,
            duration: data.duration_formatted,
            voice_name: voiceName
          }
        });
        
        // Clear the timeout since job completed
        if (window.ttsTimeoutId) {
          clearTimeout(window.ttsTimeoutId);
          window.ttsTimeoutId = null;
        }
        // Clear long text timeout as well
        if (window.ttsLongTextTimeoutId) {
          clearTimeout(window.ttsLongTextTimeoutId);
          window.ttsLongTextTimeoutId = null;
        }
        
        // Clear synthesizing state and update progress
        setIsSynthesizing(false);
        setTtsProgress({
          status: 'completed',
          queuePosition: null,
          message: 'Speech ready!'
        });
        
        // Clear progress after 5 seconds
        setTimeout(() => {
          setTtsProgress({
            status: null,
            queuePosition: null,
            message: ''
          });
        }, 5000);
        
        // Check for word timings if not already set
        if (data.word_timings && !wordTimings) {
          console.log('[Translation] Received word timings from S3 upload:', data.word_timings);
          setWordTimings(data.word_timings);
        }
        
        // Update the history item immediately to show audio icon
        if (selectedTranslationId) {
          setTranslationHistory(prev => prev.map(item => 
            item.id === selectedTranslationId 
              ? { ...item, has_tts: true, tts_job: data.job_id }
              : item
          ));
        }
        
        // Expand the translation in history immediately
        expandTranslationInHistory(selectedTranslationId);
        
        if (selectedTranslationId) {
          // Also refresh the selected translation details to get the audio
          fetchTranslationDetails(selectedTranslationId);
        }
        
        // Refresh translation history
        fetchTranslationHistory(false, historySearchQuery, historyDateRange);
        
        // Unsubscribe from this job
        if (unsubscribeFromJob) {
          unsubscribeFromJob(data.job_id);
        }
      } else if (data.type === 'tts_job_update' && data.status === 'completed') {
        // Handle update event with completed status
        const audioUrl = data.audio_file_url || data.audio_url;
        debugLog('TTS job update - completed', {
          audioUrl,
          data: data
        });
        
        if (audioUrl) {
          console.log('[Translation] TTS update completed, setting audio URL:', audioUrl);
          setInlineAudioUrl(audioUrl);
          setShowInlinePlayer(true);
          setIsNewlyGeneratedAudio(true); // This is newly generated audio
          
          // Set up the audio with metadata for the unified player
          const voiceName = voices?.find(v => v.voice_id === selectedVoice)?.name || selectedVoice || '';
          setCurrentlyPlayingAudio({
            audio_url: audioUrl,
            word_timings: data.word_timings || wordTimings || null,
            translated_text: translationResult?.translated_text || '',
            title: 'Translation Audio',
            item_id: null,
            metadata: {
              date: new Date().toLocaleDateString(),
              time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
              source_language: sourceLanguage,
              target_language: targetLanguage,
              char_count: translationResult?.translated_text?.length || 0,
              duration: data.duration_formatted,
              voice_name: voiceName
            }
          });
        }
      } else if (data.type === 'tts_job_failed') {
        debugLog('TTS job failed', {
          error: data.error,
          data: data
        });
        
        console.error('[Translation] TTS failed:', data.error);
        setError(t.err_speech_failed_prefix + ' ' + (data.error || t.err_unknown_error));
        setShowInlinePlayer(false);
        setIsSynthesizing(false);
        setTtsProgress({
          status: null,
          queuePosition: null,
          message: ''
        });
        
        // Clear timeouts on failure
        if (window.ttsTimeoutId) {
          clearTimeout(window.ttsTimeoutId);
          window.ttsTimeoutId = null;
        }
        if (window.ttsLongTextTimeoutId) {
          clearTimeout(window.ttsLongTextTimeoutId);
          window.ttsLongTextTimeoutId = null;
        }
        
        // Unsubscribe from this job
        if (unsubscribeFromJob) {
          unsubscribeFromJob(data.job_id);
        }
      } else {
        debugLog('Other WebSocket event', {
          type: data.type,
          status: data.status,
          data: data
        });
      }
    }

    // Handle STT job updates (for audio transcription + translation)
    if (data.job_id && String(data.job_id) === String(currentSTTJobId)) {
      console.log('[Universal Translator] STT WebSocket message for our job:', data.type, data.status);

      if (data.type === 'stt_job_update' || data.type === 'stt_job_completed') {
        // Check if job reached 'delivered' status - trigger success animation
        if (data.status === 'delivered') {
          console.log('[Universal Translator] STT job delivered - showing success animation');

          // Update input text with transcription if available
          if (data.transcription) {
            setInputText(data.transcription);
            console.log('[Universal Translator] Updated input text from STT result');

            // Calculate transcription cost: 1 credit per character
            const transcribedChars = data.transcription.length;

            // If translation is performed, we charge for BOTH transcription AND translation of the same text
            // Transcription: 1 credit/char, Translation: 1 credit/char = 2 credits/char total
            const hasTranslation = !!data.translation;
            const totalCost = hasTranslation ? (transcribedChars * 2) : transcribedChars;

            // Store cost breakdown in a way that can be displayed
            const costBreakdown = {
              total: totalCost,
              transcription: transcribedChars,
              translation: hasTranslation ? transcribedChars : 0,
              sourceCharCount: transcribedChars
            };

            setTranscriptionCost(costBreakdown);
            console.log(`[Universal Translator] STT Cost - Source: ${transcribedChars} chars, Transcription: ${transcribedChars} credits, Translation: ${hasTranslation ? transcribedChars : 0} credits, Total: ${totalCost} credits`);
          }

          // Set translation result if available
          if (data.translation) {
            const translationData = {
              success: true,
              translated_text: data.translation,
              original_text: data.transcription || inputText,
              source_language: data.detected_language || sourceLanguage,
              target_language: targetLanguage,
              service_used: translationEngine,
              id: data.translation_history_id
            };
            setTranslationResult(translationData);
            translationResultRef.current = translationData;
            setTranslationJustCompleted(true);
            setTimeout(() => setTranslationJustCompleted(false), 3500);
            // Signals to the UI that Generate Speech is the user's next
            // click; the flag stays true until they act (see the
            // audioUrl / inputText effects below).
            setTranslateRanThisSession(true);

            // Expand translation in history if we have an ID
            if (data.translation_history_id) {
              setSelectedTranslationId(data.translation_history_id);
              expandTranslationInHistory(data.translation_history_id);
              // Refresh history to show the new entry
              fetchTranslationHistory(false, historySearchQuery, historyDateRange);
            }
          }

          // Stop loading and show success
          setIsTranscribing(false);
          setShowTranscriptionSuccess(true);
          setTimeout(() => {
            setShowTranscriptionSuccess(false);
          }, 3000);

          // Clear the current job ID
          setCurrentSTTJobId(null);
        }
      }

      // Check if job failed - stop loading animation
      if (data.type === 'stt_job_failed' || data.status === 'failed') {
        console.log('[Universal Translator] STT job failed - stopping loading animation');
        setIsTranscribing(false);
        setShowTranscriptionSuccess(false);
        setCurrentSTTJobId(null);
        setError(data.error || t.err_transcription_failed);
      }
    }
  }, [currentTtsJobId, currentSTTJobId, wordTimings, selectedTranslationId, translationResult, playAudio, autoPlayAfterTranslation, sourceLanguage, targetLanguage, translationEngine, inputText, historySearchQuery, historyDateRange, isSynthesizing]); // currentTtsJobIdRef, subscribeToJob, unsubscribeFromJob are used but not in deps (intentional for immediate access)

  // Initialize WebSocket
  const { subscribeToJob, unsubscribeFromJob } = useWebSocketUpdates(handleWebSocketMessage);

  // Cleanup on unmount or when job changes
  useEffect(() => {
    return () => {
      if (currentTtsJobId && unsubscribeFromJob) {
        unsubscribeFromJob(currentTtsJobId);
      }
      currentTtsJobIdRef.current = null; // Clear the ref
    };
  }, [currentTtsJobId, unsubscribeFromJob]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDatePicker]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadMenu && !event.target.closest('.download-menu-container')) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDownloadMenu]);

  const fetchSupportedLanguages = async () => {
    try {
      const response = await api.get('/v1/translate/languages/');
      setSupportedLanguages({
        source_languages: response.data.source_languages || [],
        target_languages: response.data.target_languages || [],
        available_services: response.data.available_services || []
      });
      
      if (response.data.default_source !== undefined) {
        setSourceLanguage(response.data.default_source);
      }
      if (response.data.default_target) {
        setTargetLanguage(response.data.default_target);
      }
    } catch (err) {
      console.error('Failed to fetch supported languages:', err);
    }
  };
  // This space intentionally left blank - fetchTranslationHistory moved above


  const fetchVoices = async () => {
    try {
      // Fetch user's personal voices from My Voices endpoint
      const response = await api.get('/v1/users/my-voices/');
      if (response.data.voices) {
        const fetched = response.data.voices;
        setVoices(fetched);

        // Reconcile the selected voice with the current My Voices. A persisted
        // selection (localStorage) can point to a voice no longer in My Voices;
        // if so it must NOT be submitted (it would route to the wrong service).
        // Reset to the first available voice, or clear if none.
        const stillValid = selectedVoice && fetched.some(v => v.voice_id === selectedVoice);
        if (!stillValid) {
          setSelectedVoice(fetched.length > 0 ? fetched[0].voice_id : null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch voices:', err);
      // Fallback to empty array if fetch fails
      setVoices([]);
    }
  };

  // ── Real-time playback Pro-voice enforcement ──────────────────────
  // Real-time playback runs on Creole Studio Pro (ccl_ht_gem_pro) only.
  // The user's selected voice must be a Pro voice at S2S-submit time
  // or the backend rejects the job. Pair the non-Pro Studio catalog
  // (ccl_ht_gem) with the Pro catalog by voice NAME — the two tiers
  // ship matching casts (Berthie, Carline, Junior, etc.) so mapping
  // name → Pro-voice-id is stable and non-lossy.
  const proVoices = (voices || []).filter((v) => (v?.supported_models || []).includes('ccl_ht_gem_pro'));
  const findProVoiceForName = (name) => {
    if (!name) return null;
    const hit = proVoices.find((v) => v?.name === name);
    return hit ? hit.voice_id : null;
  };
  // When Real-time is on AND the user's selection isn't Pro-tier,
  // auto-upgrade to the matching Pro voice. If no name match exists
  // (e.g. a custom-cloned voice), fall back to the first Pro voice
  // so the S2S submit never sends a non-Pro voice_id and never 400s.
  useEffect(() => {
    if (!realtimePlayback || targetLanguage !== 'ht') return;
    if (!hasProAccess()) return;
    if (!voices || voices.length === 0 || !selectedVoice) return;
    const current = voices.find((v) => v.voice_id === selectedVoice);
    if (!current) return;
    const isPro = (current.supported_models || []).includes('ccl_ht_gem_pro');
    if (isPro) return;
    const upgraded = findProVoiceForName(current.name) || proVoices[0]?.voice_id;
    if (upgraded && upgraded !== selectedVoice) {
      console.log('[Translate S2S] auto-upgrading voice for Real-time playback:',
        current.name, current.voice_id, '→', upgraded);
      setSelectedVoice(upgraded);
    }
    // proVoices/findProVoiceForName are derived from `voices` — no need
    // to list them as deps (would re-run on every render since they're
    // re-computed each time). realtimePlayback + selectedVoice + voices
    // + userPlan are the only inputs that gate this decision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtimePlayback, targetLanguage, selectedVoice, voices, userPlan]);

  // Clear the cached Real-time flag if the user no longer has Pro access.
  // realtimePlayback persists in localStorage across sessions — a
  // downgraded user would otherwise return with realtimePlayback=true
  // and hit the S2S branch in handleTranslate (which fails server-side
  // when they can't submit ccl_ht_gem_pro). Wait for the plan fetch to
  // finish first (userPlan!=null) so we don't clear on initial mount
  // while /v1/credits/subscription/ is still in flight.
  useEffect(() => {
    if (userPlan === null) return;
    if (realtimePlayback && !hasProAccess()) {
      console.log('[Translate] clearing Real-time playback flag — plan lacks Pro access');
      setRealtimePlayback(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlan, realtimePlayback]);

  const fetchUserPlan = async () => {
    if (!authToken) {
      setPlanLoading(false);
      return;
    }
    
    setPlanLoading(true);
    try {
      // Get user subscription info from credits API
      const response = await axios.get('/v1/credits/subscription/', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data && response.data.plan) {
        // Extract plan type from the subscription data
        const planType = response.data.plan.plan_type;
        debugLog('User plan from API', { planType, normalized: planType.toLowerCase() });
        setUserPlan(planType.toLowerCase());
        
        // Set character limit based on plan
        const planCharLimit = response.data.plan.max_characters_per_request;
        if (planCharLimit && planCharLimit !== maxCharsLimit) {
          setMaxCharsLimit(planCharLimit);
          console.log('Updated character limit based on plan:', planCharLimit);
        }
      }
    } catch (error) {
      // If 404 or error, user has no subscription (free plan)
      if (error.response && error.response.status === 404) {
        setUserPlan('free');
        setMaxCharsLimit(1000); // Free plan limit
        console.log('User on free plan, character limit: 1000');
      } else {
        console.error('[Translation] Failed to fetch user plan:', error);
        setUserPlan('free');
      }
    } finally {
      setPlanLoading(false);
    }
  };


  const openDownloadModal = () => {
    debugLog('openDownloadModal called', {
      hasTranslationResult: !!translationResult,
      hasTtsJobDetails: !!translationResult?.tts_job_details,
      ttsJobDetails: translationResult?.tts_job_details,
      showDownloadModal: showDownloadModal
    });
    if (!translationResult?.tts_job_details) return;
    setShowDownloadModal(true);
    debugLog('showDownloadModal set to true');
  };

  const handleDownloadFromModal = async (settings) => {
    if (!settings.job || !settings.job.audio_url) return;
    
    // Convert S3 URL to proxy URL with format conversion parameters
    const proxyUrl = getAudioProxyUrl(settings.job.audio_url);
    
    // For local audio files (Express mode), don't add format conversion params
    // as the backend doesn't support conversion for local files yet
    let downloadUrl = proxyUrl;
    if (!settings.job.audio_url.includes('/v1/tts/audio/local/')) {
      const params = new URLSearchParams({
        format: settings.format,
        quality: settings.quality,
        sample_rate: settings.quality,
        bit_rate: settings.bitrate
      });
      
      // Build the URL with parameters
      const separator = proxyUrl.includes('?') ? '&' : '?';
      downloadUrl = `${proxyUrl}${separator}${params}`;
    }
    
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Create the full filename
      const fullFilename = `${settings.filename}.${settings.format}`;
      
      // For browsers that support the File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: fullFilename,
            types: [{
              description: 'Audio Files',
              accept: { [`audio/${settings.format}`]: [`.${settings.format}`] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
        } catch (err) {
          if (err.name !== 'AbortError') {
            throw err;
          }
        }
      } else {
        // Fallback for browsers that don't support File System Access API
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fullFilename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      }
      
      // Save preferences if requested
      if (settings.savePreferences) {
        const prefsToSave = {
          folder: settings.folder,
          format: settings.format,
          quality: settings.quality,
          bitrate: settings.bitrate
        };
        localStorage.setItem('ttsDownloadPreferences', JSON.stringify(prefsToSave));
      }
      
      // Close modal
      setShowDownloadModal(false);
    } catch (error) {
      console.error('Download failed:', error);
      alert(t.err_download_audio);
    }
  };

  const handleClear = () => {
    // Clear all form data
    setInputText('');
    setSourceLanguage('');
    setTargetLanguage('ht');
    setTranslationEngine('auto');
    setTranslationResult(null);
    setShowTTSOptions(false);
    setSelectedVoice(null);
    setSelectedTranslationId(null);
    setAudioUrl(null);
    setError(null);
    setShowInlinePlayer(false);
    setInlineAudioUrl(null);
    setTtsProgress({ status: null, queuePosition: null, message: '' });
    setIsNewlyGeneratedAudio(false);
    
    // Clear localStorage
    try {
      localStorage.removeItem('translationFormData');
    } catch (e) {
      console.error('[Translation] Failed to clear saved form data:', e);
    }
  };

  const handleDeleteAudio = async () => {
    if (!translationResult?.tts_job_details?.id) return;
    
    debugLog('Deleting audio job:', translationResult.tts_job_details.id);
    
    try {
      const response = await axios.delete(`/v1/tts/jobs/${translationResult.tts_job_details.id}/`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      debugLog('Delete response:', response.data);
      
      // Clear the TTS details from the translation result
      setTranslationResult(prev => ({
        ...prev,
        tts_job_details: null
      }));
      
      // Clear audio-related state
      setInlineAudioUrl(null);
      setShowInlinePlayer(false);
      setWordTimings(null);
      setIsNewlyGeneratedAudio(false);
      setHighlightedWordIndex(-1);
      setTtsProgress({ status: null, queuePosition: null, message: '' });
      setCurrentTtsJobId(null);
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      
      // Refresh credit balance
      if (window.refreshPlanCredits) window.refreshPlanCredits();
      
      // Refresh translation history if this was from a history item
      if (selectedTranslationId) {
        fetchTranslationHistory(false, historySearchQuery, historyDateRange);
      }
    } catch (error) {
      console.error('Failed to delete audio:', error);
      alert(t.err_delete_audio);
      setShowDeleteConfirm(false);
    }
  };

  // Handler for history item actions
  const handleHistoryItemAction = (action, item) => {
    debugLog('History item action:', { action, itemId: item.id });
    
    switch (action) {
      case 'download':
        if (item.tts_job_details?.audio_url) {
          setSelectedTranslationId(item.id);
          setTranslationResult(item);
          openDownloadModal();
        }
        break;
      case 'export':
        if (item) {
          setSelectedTranslationId(item.id);
          setTranslationResult(item);
          setShowExportModal(true);
        }
        break;
      case 'delete':
        if (item.tts_job_details?.id) {
          setSelectedTranslationId(item.id);
          setTranslationResult(item);
          setShowDeleteConfirm(true);
        }
        break;
      case 'delete-entry':
        setEntryToDelete(item);
        setShowDeleteEntryConfirm(true);
        break;
    }
  };

  // Handler for deleting a translation entry
  const handleDeleteEntry = async () => {
    if (!entryToDelete?.id) return;
    
    debugLog('Deleting translation entry:', entryToDelete.id);
    
    try {
      await api.delete(`/v1/translate/history/${entryToDelete.id}/`);
      
      debugLog('Translation entry deleted successfully');
      
      // Clear selection if this was the selected item
      if (selectedTranslationId === entryToDelete.id) {
        setSelectedTranslationId(null);
        setTranslationResult(null);
        setShowTTSOptions(false);
        setShowInlinePlayer(false);
        setInlineAudioUrl(null);
        setTtsProgress({ status: null, queuePosition: null, message: '' });
        setIsNewlyGeneratedAudio(false);
      }
      
      // Close the confirmation dialog
      setShowDeleteEntryConfirm(false);
      setEntryToDelete(null);
      
      // Refresh translation history
      fetchTranslationHistory(false, historySearchQuery, historyDateRange);
      
      // Show success message (optional)
      debugLog('Translation entry deleted and history refreshed');
    } catch (error) {
      console.error('Failed to delete translation entry:', error);
      alert(t.err_delete_entry);
      setShowDeleteEntryConfirm(false);
      setEntryToDelete(null);
    }
  };

  // ─── Background-synthesis state ───────────────────────────────────
  // Real-time playback pre-generates translated Creole audio DURING
  // recording, so on Stop we can play back a smoothly-assembled buffer
  // instead of gambling on Gemini's mid-stream SSE cadence. See the vendor-name-ok: dev-only architectural comment
  // synth orchestrator below for how these get filled.
  const bufferedPcmSegmentsRef = useRef([]);      // {pcm, sampleRate, text, jobId}[]
  const bufferedTotalSecsRef = useRef(0);         // sum of segments' durations
  const lastFullTranslationRef = useRef('');      // last cumulative translation we saw
  const synthedCursorRef = useRef(0);             // char count of translation already claimed for synth
  const bgSynthInFlightRef = useRef(false);
  const lastBgSynthAtRef = useRef(0); // wall clock ms of last successful bg synth kickoff
  // Match the mic recorder's 5-min hard cap. Real-time recordings are
  // bounded there anyway (Gemini's 25 MB inline-audio ceiling), and a vendor-name-ok: dev-only architectural comment
  // lower buffer cap just leaves the tail of long recordings without
  // audio while translations continue to grow (repro: 60 s English →
  // 22 s Creole because we stopped synthing at 15 s).
  const MAX_BUFFER_SECS = 300;
  // Batch bg synth to reduce audible seams between segments. Each Gemini vendor-name-ok: dev-only architectural comment
  // TTS call generates its own prosody envelope, so concatenating many
  // small clips produces a spliced feel. Wait until we have 2+ complete
  // sentences OR the user has been quiet for 10s+ before firing.
  const MIN_SENTENCES_PER_SYNTH = 2;
  const MAX_WAIT_MS_FOR_BATCH = 10000;

  // Split a text into complete sentences (chunks ending in one of . ! ? …
  // or newline). Anything after the last terminator is a fragment and is
  // returned as `remainder` so it can wait for the next round when it
  // (hopefully) becomes a full sentence. Character-set match — Creole
  // punctuation is the same ASCII terminators plus ellipsis.
  const splitOnSentenceBoundaries = (text) => {
    if (!text) return { sentences: [], matchedChars: 0 };
    const re = /[^.!?…\n]*[.!?…\n]+/g;
    const sentences = [];
    let matchedChars = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      const s = m[0].trim();
      if (s.length > 0) sentences.push(s);
      matchedChars = m.index + m[0].length;
    }
    return { sentences, matchedChars };
  };

  // Fire a streaming TTS job, collect ALL PCM chunks client-side into a
  // single Float32Array, and return it plus the job ID. Used both for
  // background synth during recording AND for the tail synth on Stop.
  // Does NOT play — the caller decides when/how to schedule the result.
  const synthToPcmBuffer = useCallback(async (text) => {
    if (!text || text.trim().length === 0) return null;
    const submitResp = await api.post('/v1/tts/jobs/', {
      text,
      voice_id: selectedVoice || DEFAULT_S2S_CREOLE_VOICE_ID,
      model_id: S2S_MODEL_ID,
      inference_mode: 'streaming',
      language_code: 'ht',
    }, {
      headers: { Authorization: `Bearer ${authToken}` },
      timeout: 30000,
    });
    const jobData = submitResp?.data;
    if (!jobData?.stream_url) return null;
    const jobId = jobData.id;
    return await new Promise((resolve, reject) => {
      const es = new EventSource(jobData.stream_url);
      const chunks = [];
      const rawB64Chunks = []; // buffer raw base64; decode on 'done'
      let totalSamples = 0;
      let sampleRate = 24000;
      const t0 = performance.now();
      let firstChunkLogged = false;
      es.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'open') {
            if (msg.sample_rate) sampleRate = msg.sample_rate;
            console.log('[synthToPcmBuffer] SSE open at t+',
              (performance.now() - t0).toFixed(0) + 'ms jobId=', jobId);
          } else if (msg.type === 'chunk' && msg.pcm_b64) {
            if (!firstChunkLogged) {
              firstChunkLogged = true;
              console.log('[synthToPcmBuffer] first PCM chunk at t+',
                (performance.now() - t0).toFixed(0) + 'ms jobId=', jobId);
            }
            // Defer heavy work to 'done'. Previously we base64-decoded and
            // Int16→Float32-converted every chunk in the SSE handler; that
            // fired 1-2 s after Stop and the concurrent CPU work on the
            // main thread starved Safari's audio thread — the leading
            // suspect for the consistent 1-2 s playback hiccup. Buffer
            // the raw base64 strings now, do ALL decoding once at 'done'
            // after playback of the pre-buffered head is well underway.
            rawB64Chunks.push(msg.pcm_b64);
          } else if (msg.type === 'done') {
            console.log('[synthToPcmBuffer] done at t+',
              (performance.now() - t0).toFixed(0) + 'ms, decoding',
              rawB64Chunks.length, 'chunks jobId=', jobId);
            for (const b64 of rawB64Chunks) {
              const binary = atob(b64);
              const len = binary.length;
              const bytes = new Uint8Array(len);
              for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
              const sampleCount = Math.floor(len / 2);
              if (sampleCount === 0) continue;
              const dv = new DataView(bytes.buffer);
              const floats = new Float32Array(sampleCount);
              for (let i = 0; i < sampleCount; i++) {
                floats[i] = dv.getInt16(i * 2, true) / 32768;
              }
              chunks.push(floats);
              totalSamples += sampleCount;
            }
            try { es.close(); } catch (_) { /* noop */ }
            const assembled = new Float32Array(totalSamples);
            let off = 0;
            for (const c of chunks) { assembled.set(c, off); off += c.length; }
            resolve({ pcm: assembled, sampleRate, jobId });
          } else if (msg.type === 'error' || msg.type === 'cancelled') {
            try { es.close(); } catch (_) { /* noop */ }
            reject(new Error(`Stream ${msg.type}: ${msg.code || 'unknown'}`));
          }
        } catch (parseErr) {
          console.warn('[Translate S2S synth] bad frame:', parseErr);
        }
      };
      es.onerror = () => {
        try { es.close(); } catch (_) { /* noop */ }
        reject(new Error('EventSource error during synth'));
      };
    });
  }, [authToken, selectedVoice]);

  // Reset the background-synth state. Called when we detect a fresh
  // recording session (translation is shorter than what we've seen)
  // or when we've consumed the buffer at Stop.
  const resetBackgroundSynthState = useCallback(() => {
    bufferedPcmSegmentsRef.current = [];
    bufferedTotalSecsRef.current = 0;
    lastFullTranslationRef.current = '';
    synthedCursorRef.current = 0;
    bgSynthInFlightRef.current = false;
  }, []);

  // Runs synchronously inside the mic's Start Recording click — this is
  // the ONE moment we're guaranteed to be inside a user gesture, which
  // is what Safari needs to let AudioContext.resume() succeed. Priming
  // here fixes:
  //   1. First-recording-after-page-refresh silence — the checkbox's
  //      localStorage-restored `checked=true` isn't a gesture, so
  //      handleRealtimeAudioReady's later prime() call was too late.
  //   2. "Old session's audio plays on next Start" — resetForNewSession()
  //      cancels any AudioBufferSourceNodes that were scheduled during
  //      the previous take but never actually played (context stayed
  //      suspended).
  const handleRealtimeRecordingStart = useCallback(() => {
    liveStream.prime();
    const streamer = liveStream.pcmStreamerRef.current;
    if (streamer && streamer.resetForNewSession) {
      streamer.resetForNewSession();
    }
    resetBackgroundSynthState();
    setInterimSpeechText('');
  }, [liveStream, resetBackgroundSynthState]);

  // Orchestrator: examine the latest cumulative translation, detect
  // new complete sentences beyond what we've already claimed, and fire
  // a background synth if we're under the 15 s cap and no synth is
  // in flight. Called after each background STT+translate call.
  //
  // opts.force=true (Stop-time flush): bypass the batching threshold
  // AND fire on a trailing fragment even if it has no terminator, so
  // the last spoken sentence gets synthed. Without this, a lone-sentence
  // recording (or the last sentence of a longer take) sits below
  // MIN_SENTENCES_PER_SYNTH forever and never reaches audio.
  const tryFireBackgroundSynth = useCallback((translatedText, opts = {}) => {
    if (!translatedText) return;
    const { force = false } = opts;

    // Revision detection must run BEFORE the in-flight / cap gates.
    // Otherwise: once we hit MAX_BUFFER_SECS, the early return skips
    // this block, lastFullTranslationRef stops updating, and a late
    // revision would never be noticed → audio and transcript drift
    // apart (repro: 60 s recording → 22 s Creole audio because
    // late-recording revisions kept updating preText but no revision
    // reset ever fired to unblock more synths).
    const priorFull = lastFullTranslationRef.current;
    const priorClaimed = priorFull.slice(0, synthedCursorRef.current);
    if (priorClaimed && !translatedText.startsWith(priorClaimed)) {
      console.log('[Translate S2S] translation revised, resetting buffer');
      resetBackgroundSynthState();
    }
    lastFullTranslationRef.current = translatedText;

    if (bgSynthInFlightRef.current) return;
    if (bufferedTotalSecsRef.current >= MAX_BUFFER_SECS) return;

    // Look for complete sentences in the delta since our last claim.
    const delta = translatedText.slice(synthedCursorRef.current);
    if (delta.length === 0) return;
    const { sentences, matchedChars } = splitOnSentenceBoundaries(delta);
    // On Stop-flush, if there are no terminated sentences left, also
    // include any trailing fragment so a final sentence spoken without
    // a period ("hello world how are you") still reaches audio.
    let synthSentences = sentences;
    let synthMatched = matchedChars;
    if (force && sentences.length === 0) {
      const tail = delta.trim();
      if (!tail) return;
      synthSentences = [tail];
      synthMatched = delta.length;
    } else if (sentences.length === 0) {
      return;
    }

    // Batch to reduce concatenation seams: require MIN_SENTENCES_PER_SYNTH
    // unless it's been MAX_WAIT_MS_FOR_BATCH since the last successful
    // synth (in which case ship what we have to avoid ballooning latency
    // for slow speakers). Stop-flush (force=true) always bypasses this.
    const now = Date.now();
    const timeSinceLast = now - lastBgSynthAtRef.current;
    if (!force && synthSentences.length < MIN_SENTENCES_PER_SYNTH && timeSinceLast < MAX_WAIT_MS_FOR_BATCH) {
      console.log('[Translate S2S] bg synth: batching — have',
        synthSentences.length, 'sentences, need', MIN_SENTENCES_PER_SYNTH,
        '(or wait', ((MAX_WAIT_MS_FOR_BATCH - timeSinceLast)/1000).toFixed(1) + 's)');
      return;
    }

    const textToSynth = synthSentences.join(' ').trim();
    if (!textToSynth) return;
    lastBgSynthAtRef.current = now;

    // Claim the cursor position immediately so a concurrent round
    // can't re-queue the same text.
    bgSynthInFlightRef.current = true;
    const claimedCursor = synthedCursorRef.current + synthMatched;

    (async () => {
      console.log('[Translate S2S] bg synth: submitting',
        JSON.stringify(textToSynth).slice(0, 120));
      try {
        const result = await synthToPcmBuffer(textToSynth);
        if (result && result.pcm && result.pcm.length > 0) {
          bufferedPcmSegmentsRef.current.push({
            pcm: result.pcm,
            sampleRate: result.sampleRate,
            text: textToSynth,
            jobId: result.jobId,
          });
          bufferedTotalSecsRef.current += result.pcm.length / result.sampleRate;
          synthedCursorRef.current = claimedCursor;
          console.log('[Translate S2S] bg synth: DONE — segments=',
            bufferedPcmSegmentsRef.current.length,
            'total buffered=', bufferedTotalSecsRef.current.toFixed(2) + 's',
            'synthedCursor=', synthedCursorRef.current);
        } else {
          console.warn('[Translate S2S] bg synth: EMPTY result (no PCM)');
        }
      } catch (err) {
        console.warn('[Translate S2S] bg synth: FAILED —',
          err?.message || err, err?.response?.data);
      } finally {
        bgSynthInFlightRef.current = false;
      }
    })();
  }, [resetBackgroundSynthState, synthToPcmBuffer]);

  // ─── Speech-to-Speech reusable helpers ────────────────────────────
  // These take the input (blob/text) as direct arguments so callers
  // that fire outside React's render cycle (mic onRecordingComplete,
  // future file-drop-auto) don't race the state update. Each helper
  // sets/clears its own error state but leaves isTranslating to the
  // caller — the outer handleTranslate + handleRealtimeAudioReady own
  // that lifecycle.

  const runS2SFromText = useCallback(async (translatedText, opts = {}) => {
    if (!translatedText) return { delivered: 0, aborted: true, firstJobId: null };
    const { translationId = null } = opts;
    const phrases = splitTextForStreaming(translatedText, TTS_DEFAULTS.STREAMING_MAX_INPUT_CHARS);
    // Capture the FIRST delivered TTS job's ID so we can PATCH the
    // Translation record afterward (Translation.tts_job FK). For batched
    // submissions we still only link the first phrase — the History UI
    // will play that phrase's audio; concatenated multi-phrase audio is
    // a follow-up (would require server-side WAV concat).
    let firstJobId = null;
    const submitPhrase = async (phraseText, index) => {
      const payload = {
        text: phraseText,
        voice_id: selectedVoice || DEFAULT_S2S_CREOLE_VOICE_ID,
        model_id: S2S_MODEL_ID,
        inference_mode: 'streaming',
        language_code: 'ht',
        ...(phrases.length > 1 ? {
          batch_id: crypto.randomUUID ? crypto.randomUUID() : `batch-${Date.now()}`,
          batch_sequence: index,
          batch_total: phrases.length,
        } : {}),
      };
      const submitResp = await api.post('/v1/tts/jobs/', payload, {
        headers: { Authorization: `Bearer ${authToken}` },
        timeout: 30000,
      });
      if (index === 0 && submitResp?.data?.id) firstJobId = submitResp.data.id;
      return submitResp?.data;
    };
    const result = await liveStream.streamBatch(phrases, submitPhrase);
    // Link the delivered TTS job back to the Translation row for History
    // playback. Best-effort — a failure here doesn't affect the played
    // audio the user just heard.
    if (translationId && firstJobId && result.delivered > 0) {
      try {
        await api.patch(
          `/v1/translate/history/${translationId}/`,
          { tts_job_id: firstJobId },
          { headers: { Authorization: `Bearer ${authToken}` }, timeout: 15000 }
        );
      } catch (linkErr) {
        console.warn('[Translate S2S] history link PATCH failed:', linkErr);
      }
    }
    return { ...result, firstJobId };
  }, [authToken, liveStream, selectedVoice]);

  // Background-translation call fired every ~4 s during mic recording
  // when Real-time playback is on. `charge=false` + `save_to_history=false`
  // keeps intermediate calls free/silent — only the final Stop-triggered
  // path bills the user. Returns just the translated text (or null on
  // failure — a failed intermediate is logged and swallowed so recording
  // continues uninterrupted).
  // Low-level POST to /v1/translate/speech/ with no side effects. Used
  // by the Stop-time catchup path so it can grab the LATEST full
  // translation (mic Stop happens mid-way through the ~4s bg-call
  // window, so bgLatestTextRef is always missing the last few seconds
  // of speech) without accidentally kicking off another background
  // synth cycle via tryFireBackgroundSynth. Returns the response data
  // or null.
  const rawSpeechTranslate = useCallback(async (audioBlob, opts = {}) => {
    if (!audioBlob || audioBlob.size === 0) return null;
    const { sourceLang = 'auto', targetLang = 'ht', charge = false, save = false } = opts;
    const form = new FormData();
    form.append('audio', audioBlob);
    form.append('source_language', sourceLang);
    form.append('target_language', targetLang);
    form.append('save_to_history', save ? 'true' : 'false');
    form.append('charge', charge ? 'true' : 'false');
    try {
      const resp = await api.post('/v1/translate/speech/', form, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      return resp?.data || null;
    } catch (err) {
      console.warn('[Translate S2S rawSpeechTranslate] failed:', err?.message || err);
      return null;
    }
  }, [authToken]);

  const runBackgroundSpeechChunk = useCallback(async (audioBlob, sourceLang = 'auto', targetLang = 'ht') => {
    if (!audioBlob || audioBlob.size === 0) return null;
    const form = new FormData();
    form.append('audio', audioBlob);
    form.append('source_language', sourceLang || 'auto');
    form.append('target_language', targetLang || 'ht');
    form.append('save_to_history', 'false');
    form.append('charge', 'false');
    try {
      const resp = await api.post('/v1/translate/speech/', form, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      const translated = resp?.data?.translated_text || null;
      if (!translated) return null;
      // Kick off background TTS synth for any new complete sentence(s)
      // in the delta. Fire-and-forget — the orchestrator gates on
      // in-flight lock + 15 s cap. This is what pre-fills the buffer
      // that Stop will play from.
      try { tryFireBackgroundSynth(translated); } catch (e) { console.warn('[Translate S2S] bg synth kickoff threw:', e?.message || e); }
      // Return the full record so the mic Stop path can commit both the
      // translation AND the transcription without a fresh Gemini call. vendor-name-ok: dev-only architectural comment
      return {
        translated_text: translated,
        original_text: resp?.data?.original_text || '',
        source_language: resp?.data?.source_language || sourceLang || 'auto',
      };
    } catch (err) {
      console.warn('[Translate S2S background] chunk failed (non-fatal):', err?.message || err);
      return null;
    }
  }, [authToken, tryFireBackgroundSynth]);

  const runS2SFromAudioBlob = useCallback(async ({ audioBlob, sourceLang, targetLang }) => {
    const s2sForm = new FormData();
    s2sForm.append('audio', audioBlob);
    s2sForm.append('source_language', sourceLang || 'auto');
    s2sForm.append('target_language', targetLang || 'ht');
    const s2sResp = await api.post('/v1/translate/speech/', s2sForm, {
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    const translatedText = s2sResp?.data?.translated_text || '';
    if (!translatedText) throw new Error(t.err_no_translated_text);
    // Preserve the Translation record ID so we can link the delivered
    // TTS job back to it for History playback.
    const translationId = s2sResp?.data?.translation_id || s2sResp?.data?.id || null;
    setTranslationResult({
      id: translationId,
      original_text: s2sResp?.data?.original_text || '',
      translated_text: translatedText,
      source_language: s2sResp?.data?.source_language || sourceLang || 'auto',
      target_language: targetLang || 'ht',
      service_used: s2sResp?.data?.service_used || 'gemini_speech',
    });
    await runS2SFromText(translatedText, { translationId });
    if (window.refreshPlanCredits) window.refreshPlanCredits();
    // Post-Stop UX: auto-expand the newly-created History entry and
    // smooth-scroll it into view. Same handoff the pre-buffered branch
    // does — this fires on the fallback path (no bg synth ran, e.g. the
    // ~4s background chunk failed with a timeout, or the recording was
    // shorter than one bg cycle).
    if (translationId && typeof fetchTranslationHistory === 'function') {
      try {
        await fetchTranslationHistory(false, historySearchQuery, historyDateRange);
        expandTranslationInHistory(translationId);
      } catch (e) { console.warn('[Translate S2S] history refresh (audio-blob fallback) failed:', e?.message || e); }
    }
    return translatedText;
  }, [authToken, runS2SFromText, fetchTranslationHistory, historySearchQuery, historyDateRange]);

  // Confirmation gate for Real-time on non-mic inputs. Uses
  // window.confirm() as an MVP — clear, accessible, immediate.
  // Text > STREAMING_MAX_INPUT_CHARS (600) triggers with exact cost.
  // Audio > 500 KB (~30 s at 128 kbps) triggers with a note that
  // actual credits are computed post-transcription.
  const confirmS2SIfLarge = useCallback(({ textChars = 0, audioBytes = 0 }) => {
    const bigText = textChars > TTS_DEFAULTS.STREAMING_MAX_INPUT_CHARS;
    const bigAudio = audioBytes > 500_000;
    if (!bigText && !bigAudio) return true;
    if (bigText) {
      const credits = Math.ceil(
        textChars * TTS_DEFAULTS.CREDITS_PER_CHAR * TTS_DEFAULTS.STREAMING_MULTIPLIER
      );
      return window.confirm(
        `Real-time playback will translate and speak ${textChars.toLocaleString()} characters.\n\n` +
        `Estimated cost: ~${credits.toLocaleString()} credits (1.5x streaming rate).\n\n` +
        `Continue?`
      );
    }
    const kb = Math.round(audioBytes / 1024);
    return window.confirm(
      `Real-time playback will transcribe, translate, and speak this ${kb.toLocaleString()} KB audio clip.\n\n` +
      `Streaming rate is 1.5x standard. Actual credits are calculated after transcription.\n\n` +
      `Continue?`
    );
  }, []);

  // Fire-and-forget S2S from a fresh audio blob. Called from:
  //   - MicrophoneRecorder's onRecordingComplete when Real-time is on
  //     (skipConfirm=true — the user already chose to record).
  //   - Future file-drop-auto flow (skipConfirm=false — confirm large
  //     uploads first).
  // Owns its own isTranslating + error lifecycle so callers just fire.
  const handleRealtimeAudioReady = useCallback(async (audioBlob, opts = {}) => {
    if (!audioBlob) return;
    const {
      skipConfirm = false,
      sourceLang = null,
      targetLang = 'ht',
      preTranslated = null,
    } = opts;
    if (!skipConfirm && !confirmS2SIfLarge({ audioBytes: audioBlob.size })) {
      return;
    }
    liveStream.prime();
    setIsTranslating(true);
    setError(null);
    try {
      // ── Pre-buffered-audio path (best) ─────────────────────────────
      // Background synth during recording pre-generated Creole audio
      // for every complete sentence we saw. Hand the assembled segments
      // to the streamer for smooth, jitter-free playback. Any untrans-
      // lated tail (final incomplete sentence + last few seconds not
      // yet reflected in translation) gets a single tail synth in
      // parallel that appends when it lands.
      //
      // Stop-flush: any complete sentence(s) that were held back by the
      // MIN_SENTENCES_PER_SYNTH gate need to fire NOW so they land in
      // bufferedSegments before playback starts (otherwise they get
      // demoted to the tail synth, which races playback and often loses
      // the audio if the pre-buffered segments finish first).
      try {
        tryFireBackgroundSynth(lastFullTranslationRef.current, { force: true });
      } catch (e) {
        console.warn('[Translate S2S] Stop: force-flush errored:', e?.message || e);
      }
      // If a background synth is IN FLIGHT at Stop time (very common
      // for longer recordings — Round N might still be running when
      // the user taps Stop), wait up to 3 s for it so its output
      // makes it into the pre-buffered playback. Without this we'd
      // ship Round N-1's audio and rely on the tail synth to catch
      // up, which is slower and prone to silent failure.
      if (bgSynthInFlightRef.current) {
        console.log('[Translate S2S] Stop: awaiting in-flight bg synth (up to 3s)');
        await new Promise((resolve) => {
          const start = Date.now();
          const check = () => {
            if (!bgSynthInFlightRef.current || Date.now() - start > 3000) {
              console.log('[Translate S2S] Stop: in-flight bg synth settled after',
                Date.now() - start, 'ms; segments=', bufferedPcmSegmentsRef.current.length);
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          check();
        });
      }
      const bufferedSegments = bufferedPcmSegmentsRef.current;
      if (
        bufferedSegments.length > 0 &&
        preTranslated &&
        preTranslated.translated_text &&
        preTranslated.translated_text.trim().length > 0
      ) {
        console.log('[Translate S2S] pre-buffered path: segments=', bufferedSegments.length,
          'synthedCursor=', synthedCursorRef.current,
          'translatedTextLen=', preTranslated.translated_text.length);

        // Fire a catchup translate on the FULL audio blob in parallel with
        // playback. The last background translate call landed a few seconds
        // ago, so bgLatestTextRef is missing whatever was spoken between
        // then and Stop. Catchup lets us compute tail against the ACTUAL
        // final translation instead of the stale-4s-old snapshot. Runs
        // with charge=false + save=false — the commit at the end of this
        // path handles billing and history using the effective text.
        console.log('[Translate S2S] Stop: firing final catchup translate');
        const catchupPromise = rawSpeechTranslate(audioBlob, {
          sourceLang: sourceLang || sourceLanguage || 'auto',
          targetLang,
          charge: false,
          save: false,
        }).then((res) => {
          if (res && res.translated_text) {
            console.log('[Translate S2S] catchup: DONE — len=',
              res.translated_text.length, 'vs preTranslated len=',
              preTranslated.translated_text.length);
            return res;
          }
          console.warn('[Translate S2S] catchup: EMPTY');
          return null;
        }).catch((err) => {
          console.warn('[Translate S2S] catchup: FAILED —', err?.message || err);
          return null;
        });
        const preText = preTranslated.translated_text.trim();
        const preOriginal = (preTranslated.original_text || '').trim();

        // Start pre-buffered playback immediately — audio starts within
        // ~200 ms. Catchup + tail synth run in parallel and append to
        // the same streamer when they land.
        const streamer = liveStream.pcmStreamerRef.current
          || (() => { liveStream.prime(); return liveStream.pcmStreamerRef.current; })();
        if (streamer) {
          streamer.playPrebufferedSegments(bufferedSegments);
        }

        // Provisional UI state — refined below once catchup lands.
        setTranslationResult({
          id: null,
          original_text: preOriginal,
          translated_text: preText,
          source_language: preTranslated.source_language || sourceLang || 'auto',
          target_language: targetLang,
          service_used: 'gemini_speech_pretranslated',
        });

        // Wait for catchup, then reconcile against WHAT THE AUDIO
        // ACTUALLY SAYS. Two revision paths can lie to us:
        //   (a) `preText` (bgLatestTextRef) may be a translation newer
        //       than the last one we synth'd from — bg-latest updates
        //       on EVERY background chunk, but synth cursor only
        //       advances when we actually synth. So preText's prefix
        //       may not match the audio.
        //   (b) `catchupResult` is a fresh full-blob translate that
        //       often revises earlier words (Gemini isn't monotonic). vendor-name-ok: dev-only architectural comment
        //
        // Rule that guarantees audio == transcript: derive the
        // "audio-verified text" from the concatenated `.text` of every
        // buffered segment. Anything after that comes from the best
        // available source (catchup extension > bg tail > nothing).
        const catchupResult = await catchupPromise;
        const catchupText = (catchupResult?.translated_text || '').trim();

        // What the audio actually contains, joined with a single space
        // (matches how bg synth stitches sentence chunks).
        const audioText = bufferedSegments
          .map((s) => (s.text || '').trim())
          .filter((t) => t.length > 0)
          .join(' ');
        console.log('[Translate S2S] audioText len=', audioText.length,
          'preTextLen=', preText.length, 'catchupLen=', catchupText.length);

        let effectiveText;
        let effectiveOriginal = preOriginal;

        if (catchupText.length > audioText.length && catchupText.startsWith(audioText)) {
          // Catchup extends what audio contains — great, use it. Tail
          // will synth the extra content.
          effectiveText = catchupText;
          effectiveOriginal = (catchupResult.original_text || preOriginal || '').trim();
          console.log('[Translate S2S] adopting catchup (audio-consistent, adds',
            catchupText.length - audioText.length, 'chars)');
        } else if (preText.length > audioText.length && preText.startsWith(audioText)) {
          // Bg has more content than audio and its prefix matches
          // audio — use bg's text.
          effectiveText = preText;
          console.log('[Translate S2S] adopting preText (audio-consistent, adds',
            preText.length - audioText.length, 'chars)');
        } else {
          // Neither catchup nor preText has an audio-consistent prefix.
          // Revisions between synth and Stop rewrote earlier words.
          // Use audio's own text so transcript matches audio exactly.
          effectiveText = audioText;
          console.warn('[Translate S2S] revision detected between synth and Stop —',
            'using audioText to prevent audio/text mismatch. preTextLen=',
            preText.length, 'catchupLen=', catchupText.length,
            'audioTextLen=', audioText.length);
        }

        const tail = effectiveText.slice(audioText.length).trim();
        console.log('[Translate S2S] tail computation: effectiveLen=',
          effectiveText.length, 'audioLen=', audioText.length,
          'tailLen=', tail.length);
        // Reflect the finalised text in the UI now.
        setTranslationResult((prev) => (prev ? {
          ...prev,
          original_text: effectiveOriginal,
          translated_text: effectiveText,
        } : prev));

        // Fire tail synth (if any) in parallel — appends when it lands.
        const tailPromise = tail.length > 0
          ? (async () => {
              console.log('[Translate S2S] tail synth: submitting',
                JSON.stringify(tail).slice(0, 120));
              try {
                const r = await synthToPcmBuffer(tail);
                console.log('[Translate S2S] tail synth: DONE — samples=',
                  r?.pcm?.length || 0, 'sampleRate=', r?.sampleRate, 'jobId=', r?.jobId);
                return r;
              } catch (err) {
                console.warn('[Translate S2S] tail synth: FAILED —',
                  err?.message || err, err?.response?.data);
                return null;
              }
            })()
          : (async () => {
              console.log('[Translate S2S] tail synth: SKIP (no untranslated tail)');
              return null;
            })();

        // Commit history record via the pretranslated shortcut (no Gemini vendor-name-ok: dev-only architectural comment
        // round-trip on the backend). Uses the effective (catchup-augmented)
        // text so History shows the full transcript. Runs in parallel with
        // tail synth.
        const commitForm = new FormData();
        commitForm.append('pretranslated_text', effectiveText);
        if (effectiveOriginal) commitForm.append('pretranslated_original_text', effectiveOriginal);
        commitForm.append('source_language', preTranslated.source_language || sourceLang || 'auto');
        commitForm.append('target_language', targetLang);
        commitForm.append('save_to_history', 'true');
        commitForm.append('charge', 'true');
        const commitPromise = api.post('/v1/translate/speech/', commitForm, {
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        }).catch((err) => {
          console.warn('[Translate S2S] pretranslated commit failed (non-fatal):', err?.message || err);
          return null;
        });

        // As soon as tail synth returns, hand its buffer to the streamer.
        // Runs concurrently with the pre-buffered playback — the tail
        // schedules after the last pre-buffered segment ends.
        tailPromise.then((tailResult) => {
          if (tailResult && tailResult.pcm && tailResult.pcm.length > 0 && streamer) {
            console.log('[Translate S2S] tail synth: appending to streamer');
            streamer.appendBufferSegments([{
              pcm: tailResult.pcm,
              sampleRate: tailResult.sampleRate,
            }]);
          } else {
            console.log('[Translate S2S] tail synth: not appending (empty or no streamer). result=',
              !!tailResult, 'streamer=', !!streamer);
          }
        });

        // Snapshot segments RIGHT NOW so the later background assembly
        // sees the same set the streamer just played — resetBackgroundSynth
        // State() below will clear the underlying ref.
        const segmentsSnapshot = [...bufferedSegments];

        // Wait for commit + history link. First delivered TTS job in the
        // buffer becomes the "canonical" audio for history playback.
        const commitResp = await commitPromise;
        const translationId = commitResp?.data?.translation_id || commitResp?.data?.id || null;
        const firstJobId = bufferedSegments[0]?.jobId || null;
        if (translationId && firstJobId) {
          try {
            await api.patch(
              `/v1/translate/history/${translationId}/`,
              { tts_job_id: firstJobId },
              { headers: { Authorization: `Bearer ${authToken}` }, timeout: 15000 }
            );
            setTranslationResult((prev) => (prev ? { ...prev, id: translationId } : prev));
          } catch (linkErr) {
            console.warn('[Translate S2S] history link PATCH failed:', linkErr);
          }
        }
        if (window.refreshPlanCredits) window.refreshPlanCredits();

        // Background: after tail synth settles, assemble ALL PCM segments
        // (bg segments + tail) into a single WAV and upload for History
        // playback. The tts_job FK only points to ONE synth — this fills
        // in the full audio so History plays the complete recording.
        // Fire-and-forget: user has already heard the live audio; History
        // populates a few seconds later.
        console.log('[Translate S2S] assembled-audio: entry check —',
          'translationId=', translationId,
          'segmentsSnapshot.length=', segmentsSnapshot.length);
        if (translationId) {
          // Auto-expand the just-created History entry (and smooth-scroll
          // it into view via the useEffect on expandedHistoryRows.size).
          // Matches the non-realtime flow's post-TTS handoff — the user
          // taps Stop + Listen to Translation and their eye is led to
          // the completed row. Fetch FIRST so the row is in the list at
          // the moment we mark it expanded, then expand (React re-renders
          // with isExpanded=true on the new row).
          if (typeof fetchTranslationHistory === 'function') {
            (async () => {
              try {
                await fetchTranslationHistory(false, historySearchQuery, historyDateRange);
              } catch (e) { console.warn('[Translate S2S] history refresh (pre-buffered branch) failed:', e?.message || e); }
              expandTranslationInHistory(translationId);
            })();
          } else {
            expandTranslationInHistory(translationId);
          }
          console.log('[Translate S2S] assembled-audio: scheduling background upload');
          (async () => {
            console.log('[Translate S2S] assembled-audio: awaiting tail synth');
            const tailResult = await tailPromise;
            console.log('[Translate S2S] assembled-audio: tail settled — hasPcm=',
              !!(tailResult && tailResult.pcm && tailResult.pcm.length > 0));
            const allSegs = segmentsSnapshot.slice();
            if (tailResult && tailResult.pcm && tailResult.pcm.length > 0) {
              allSegs.push({ pcm: tailResult.pcm, sampleRate: tailResult.sampleRate });
            }
            console.log('[Translate S2S] assembled-audio: allSegs.length=', allSegs.length,
              'firstSegSamples=', allSegs[0]?.pcm?.length,
              'sampleRates=', allSegs.map((s) => s?.sampleRate));
            if (allSegs.length === 0) {
              console.warn('[Translate S2S] assembled-audio: no segments to assemble');
              return;
            }
            let wavBlob;
            try {
              wavBlob = pcmSegmentsToWavBlob(allSegs);
            } catch (encErr) {
              console.error('[Translate S2S] assembled-audio: encoder threw:', encErr);
              return;
            }
            if (!wavBlob) {
              console.warn('[Translate S2S] assembled audio: wav encode returned null');
              return;
            }
            console.log('[Translate S2S] assembled-audio: wav ready, size=', wavBlob.size, 'bytes');
            // Total assembled audio duration = sum(samples)/sampleRate.
            // Backend stores this so History displays the full assembled
            // length instead of the linked tts_job's short duration.
            let totalSamples = 0;
            let seenRate = 0;
            for (const seg of allSegs) {
              if (seg && seg.pcm && seg.pcm.length > 0) {
                totalSamples += seg.pcm.length;
                if (!seenRate && seg.sampleRate) seenRate = seg.sampleRate;
              }
            }
            const durationSecs = seenRate ? totalSamples / seenRate : null;
            const form = new FormData();
            form.append('audio', wavBlob, 'assembled.wav');
            if (durationSecs && durationSecs > 0) {
              form.append('duration_seconds', durationSecs.toFixed(3));
            }
            try {
              const uploadResp = await api.post(
                `/v1/translate/history/${translationId}/assembled-audio/`,
                form,
                {
                  headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
                  timeout: 60000,
                }
              );
              const uploadedUrl = uploadResp?.data?.assembled_audio_url;
              console.log('[Translate S2S] assembled audio uploaded:', uploadedUrl);
              // Bug fix — the History fetch that populates translationResult
              // and the AudioPlayer runs BEFORE this upload lands, so the
              // audio URL cached at that moment is the (short) tts_job's
              // audio_file_url. Force translationResult (and refresh the
              // history list) to the newly-uploaded assembled URL so the
              // AudioPlayer plays the full audio on the next click.
              if (uploadedUrl) {
                setTranslationResult((prev) => {
                  if (!prev || prev.id !== translationId) return prev;
                  return {
                    ...prev,
                    tts_job_details: {
                      ...(prev.tts_job_details || {}),
                      audio_url: uploadedUrl,
                    },
                  };
                });
                if (typeof fetchTranslationHistory === 'function') {
                  try {
                    // Re-mark the row expanded — this refetch replaces
                    // translationHistory and if the row wasn't in the
                    // first fetch (DB replica lag, etc.), we get another
                    // chance for expand to visibly stick.
                    await fetchTranslationHistory(false, historySearchQuery, historyDateRange);
                    expandTranslationInHistory(translationId);
                  } catch (e) { console.warn('[Translate S2S] history refresh (post-assembled-upload) failed:', e?.message || e); }
                }
              }
            } catch (err) {
              console.warn('[Translate S2S] assembled audio upload failed:',
                err?.message || err, 'status=', err?.response?.status, 'data=', err?.response?.data);
            }
          })().catch((outerErr) => {
            // Guard against ANY error in the async IIFE going silent.
            console.error('[Translate S2S] assembled-audio: IIFE errored:', outerErr);
          });
        } else {
          console.warn('[Translate S2S] assembled-audio: SKIP (no translationId)');
        }

        // Session done — reset for next recording.
        resetBackgroundSynthState();
        setRecordedAudio(null);
        setAudioFile(null);
        setInterimSpeechText('');
        setIsTranslating(false);
        return;
      }

      // ── Pretranslated fast-path (buffer empty — short recording) ──
      // Falls here when the recording ended before any complete sentence
      // could be background-synth'd. Same instant-playback shortcut we
      // shipped previously: use the last STT+translate for playback and
      // commit for history without a fresh Gemini call. vendor-name-ok: dev-only architectural comment
      if (
        preTranslated &&
        typeof preTranslated === 'object' &&
        preTranslated.translated_text &&
        preTranslated.translated_text.trim().length > 0
      ) {
        const translatedText = preTranslated.translated_text.trim();
        const originalText = (preTranslated.original_text || '').trim();
        setTranslationResult({
          id: null,  // populated when commit returns
          original_text: originalText,
          translated_text: translatedText,
          source_language: preTranslated.source_language || sourceLang || 'auto',
          target_language: targetLang,
          service_used: 'gemini_speech_pretranslated',
        });
        // Playback first — with the SSE stream getting the connection
        // pool to itself. Firing the commit POST at the same time was
        // making the first ~1 s of audio hiccup on some networks (both
        // requests contend for HTTP connection setup + Django worker
        // slots). Deferring the commit until after streamBatch finishes
        // costs us a few seconds of audio-duration on history linking
        // but the user hears smooth audio, which is the priority.
        const ttsResult = await runS2SFromText(translatedText);
        // Commit + link history now — user is hearing the audio (or it's
        // just finished). A commit failure here is non-fatal: user still
        // got playback; we just don't get a History entry with audio.
        const commitForm = new FormData();
        commitForm.append('pretranslated_text', translatedText);
        if (originalText) commitForm.append('pretranslated_original_text', originalText);
        commitForm.append('source_language', preTranslated.source_language || sourceLang || 'auto');
        commitForm.append('target_language', targetLang);
        commitForm.append('save_to_history', 'true');
        commitForm.append('charge', 'true');
        let commitResp = null;
        try {
          commitResp = await api.post('/v1/translate/speech/', commitForm, {
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          });
        } catch (err) {
          console.warn('[Translate S2S] pretranslated commit failed (non-fatal):', err?.message || err);
        }
        const translationId = commitResp?.data?.translation_id || commitResp?.data?.id || null;
        if (translationId && ttsResult?.firstJobId) {
          try {
            await api.patch(
              `/v1/translate/history/${translationId}/`,
              { tts_job_id: ttsResult.firstJobId },
              { headers: { Authorization: `Bearer ${authToken}` }, timeout: 15000 }
            );
            setTranslationResult((prev) => (prev ? { ...prev, id: translationId } : prev));
          } catch (linkErr) {
            console.warn('[Translate S2S] history link PATCH failed:', linkErr);
          }
        }
        if (translationId && typeof fetchTranslationHistory === 'function') {
          // Auto-expand + scroll the just-created row on the fallback path
          // too. Same post-Stop handoff as the pre-buffered branch above.
          try {
            await fetchTranslationHistory(false, historySearchQuery, historyDateRange);
            expandTranslationInHistory(translationId);
          } catch (e) { console.warn('[Translate S2S] history refresh (no-buffer branch) failed:', e?.message || e); }
        }
        if (window.refreshPlanCredits) window.refreshPlanCredits();
      } else {
        // Fallback (no background translation available — e.g. recording
        // stopped before the first ~4 s chunk fired): do the standard
        // audio→speech round-trip.
        await runS2SFromAudioBlob({
          audioBlob,
          sourceLang: sourceLang || sourceLanguage || 'auto',
          targetLang,
        });
      }
      setRecordedAudio(null);
      setAudioFile(null);
      setInterimSpeechText('');
      // Any lingering background-synth state from this recording (e.g.
      // in-flight synth for a sentence that got clobbered by revision)
      // should be cleared so the next recording starts fresh.
      resetBackgroundSynthState();
    } catch (err) {
      console.error('[Translate S2S] realtime audio failed:', err);
      setError(err?.response?.data?.detail || err.message || t.err_realtime_failed);
    } finally {
      setIsTranslating(false);
    }
  }, [authToken, confirmS2SIfLarge, liveStream, rawSpeechTranslate, resetBackgroundSynthState, runS2SFromAudioBlob, runS2SFromText, sourceLanguage, synthToPcmBuffer, tryFireBackgroundSynth]);

  const handleTranslate = async () => {
    console.log('[handleTranslate] Function called');
    debugLog('Translate button clicked!', { 
      inputTextLength: inputText.length,
      hasRecordedAudio: !!recordedAudio,
      hasAudioFile: !!audioFile,
      recordedAudioSize: recordedAudio?.size,
      inputMode 
    });
    
    // Check if we have content to translate
    if (!inputText.trim() && !recordedAudio && !audioFile) {
      setError(t.err_no_input);
      return;
    }

    // Check if selected engine is accessible
    if (!hasEngineAccess(translationEngine)) {
      setError(t.err_engine_unavailable);
      return;
    }

    // Prime the AudioContext INSIDE this user-gesture handler so Safari's
    // autoplay policy is satisfied — even if we're not doing S2S this
    // time, calling prime() is cheap and idempotent.
    liveStream.prime();

    // Real-time cost confirmation for text/document input. Audio-file
    // confirmation is inside the S2S audio branch below (once we have
    // the blob in hand). We only prompt when input is long enough to
    // actually trigger batched streaming; otherwise 1 credit/char is
    // small enough that a confirmation is friction, not value.
    if (
      realtimePlayback &&
      targetLanguage === 'ht' &&
      !recordedAudio &&
      !audioFile &&
      inputText.trim().length > 0 &&
      !confirmS2SIfLarge({ textChars: inputText.trim().length })
    ) {
      return;
    }

    setIsTranslating(true);
    setError(null);

    try {
      // ─── Speech-to-Speech fast path ─────────────────────────────────
      // Real-time playback ON + audio input + Creole target: one Gemini vendor-name-ok: dev-only architectural comment
      // multimodal call for STT+translate, then Live Stream TTS. Audio
      // starts playing within ~3-4 s. For uploads (audioFile) > 500 KB
      // we show a cost-confirmation prompt first — mic-recorded audio
      // is already gated by the user's Start/Stop clicks so we skip
      // confirmation for it.
      if (realtimePlayback && targetLanguage === 'ht' && (recordedAudio || audioFile)) {
        const audioBlob = recordedAudio
          ? new File([recordedAudio], 'recording.webm', {
              type: recordedAudio.type || 'audio/webm',
              lastModified: Date.now(),
            })
          : audioFile;
        // File uploads pass through the confirmation gate; recorded
        // audio was just captured moments ago in the user's presence.
        const skipConfirm = !audioFile;
        if (!skipConfirm && !confirmS2SIfLarge({ audioBytes: audioBlob.size })) {
          setIsTranslating(false);
          return;
        }
        try {
          await runS2SFromAudioBlob({
            audioBlob,
            sourceLang: sourceLanguage,
            targetLang: 'ht',
          });
          setRecordedAudio(null);
          setAudioFile(null);
          setIsTranslating(false);
          return;
        } catch (err) {
          console.error('[Translate S2S] fast path failed, falling through:', err);
          // Fall through to the legacy transcribe-translate flow so the
          // user still gets a transcript even if the fast path errored.
        }
      }

      // Handle audio transcription + translation if we have audio, regardless of current mode
      // (User might have recorded in voice mode but switched to text mode after transcription)
      if (recordedAudio || audioFile) {
        console.log('[handleTranslate] Audio path detected, processing audio');
        debugLog('Processing audio for transcription and translation', {
          inputMode,
          hasRecordedAudio: !!recordedAudio,
          hasAudioFile: !!audioFile,
          recordedAudioSize: recordedAudio?.size,
          audioFileSize: audioFile?.size,
          sourceLanguage,
          targetLanguage,
          translationEngine
        });
        
        const formData = new FormData();
        
        // Add audio data
        if (recordedAudio) {
          console.log('[handleTranslate] Adding recorded audio to FormData, size:', recordedAudio.size);
          formData.append('audio', recordedAudio, 'recording.webm');
        } else if (audioFile) {
          console.log('[handleTranslate] Adding audio file to FormData, size:', audioFile.size);
          formData.append('audio', audioFile);
        }
        
        // Add language settings
        const fromLang = sourceLanguage || 'auto';
        console.log('[handleTranslate] Setting from_language:', fromLang);
        formData.append('from_language', fromLang);
        
        if (targetLanguage) {
          console.log('[handleTranslate] Setting to_language:', targetLanguage);
          formData.append('to_language', targetLanguage);
        }
        
        // Add translation engine - map frontend engine keys to STT backend codes
        const engineMapping = {
          'openai_gpt-5.1': 'gpt-5.1',
          'openai_gpt-5-mini': 'gpt-5-mini',
          'anthropic_claude-sonnet-4-6': 'claude-sonnet-4-6',
          'anthropic_claude-haiku-4-5': 'claude-haiku-4-5',
          'gemini_gemini-3.5-flash': 'gemini-3.5-flash',
          'gemini_gemini-2.5-flash': 'gemini-2.5-flash',
          'google': 'google-translate',
          'libretranslate': 'libre-translate'
        };

        const backendEngine = engineMapping[translationEngine] || translationEngine;
        const validEngines = ['gpt-5.1', 'gpt-5-mini', 'claude-sonnet-4-6', 'claude-haiku-4-5', 'gemini-3.5-flash', 'gemini-2.5-flash', 'google-translate', 'libre-translate'];
        
        if (backendEngine && backendEngine !== 'auto' && validEngines.includes(backendEngine)) {
          console.log('[handleTranslate] Setting translation_engine:', backendEngine, '(from:', translationEngine, ')');
          formData.append('translation_engine', backendEngine);
        } else {
          console.log('[handleTranslate] Not setting translation_engine (auto or invalid):', translationEngine);
        }
        // If 'auto' or invalid, don't send translation_engine - let backend choose
        
        // Add transcription model (use valid choice)
        formData.append('transcription_model', 'whisper-1');
        formData.append('include_timestamps', 'false');
        
        console.log('[handleTranslate] FormData prepared with all parameters');

        // Set loading state for transcription
        setIsTranscribing(true);

        console.log('[handleTranslate] Sending audio to STT endpoint (async job flow)...');
        const response = await api.post('/v1/speech/stt/transcribe-translate/', formData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
          // No timeout - using async job flow with WebSocket updates
        });

        console.log('[handleTranslate] STT Job created:', response.data);
        debugLog('STT job initiated (async)', {
          jobId: response.data.job_id || response.data.id,
          status: response.data.status
        });

        // Store the job ID for WebSocket tracking
        const jobId = response.data.job_id || response.data.id;
        if (jobId) {
          console.log('[handleTranslate] Storing STT job ID:', jobId);
          setCurrentSTTJobId(jobId);
          // WebSocket will handle the completion and update the UI
        } else {
          console.error('[handleTranslate] No job_id received from STT endpoint');
          setError(t.err_transcribe_init_failed);
          setIsTranscribing(false);
        }

        // Clear audio immediately (don't wait for completion)
        setRecordedAudio(null);
        setRecordingDuration(0);
        setAudioFile(null);
        setTranscriptionCost(null);

        // Don't process result here - WebSocket handler will handle it
        debugLog('STT job submitted - waiting for WebSocket updates');
        
      } else if (inputText.trim()) {
        // Handle text-only translation
        console.log('[handleTranslate] Text-only path, no audio detected');
        debugLog('Translation request', {
          textLength: inputText.length,
          sourceLanguage,
          targetLanguage,
          translationEngine
        });
        
        // Build translation payload - same source/target language is allowed for grammar fixes
        const payload = {
          text: inputText,
          target_language: targetLanguage,
          save_to_history: true
        };

        if (sourceLanguage) {
          payload.source_language = sourceLanguage;
        }
        
        // Send the chosen quality tier (Standard/Advanced → Gemini 2.5/3.5). vendor-name-ok: dev-only architectural comment
        // The backend still tier-gates Advanced server-side.
        if (translationEngine) {
          payload.preferred_service = translationEngine;
        }

        debugLog('Translation request', {
          payload,
          userPlan,
          selectedEngine: translationEngine,
          hasEngineAccess: hasEngineAccess(translationEngine)
        });

        const response = await api.post('/v1/translate/', payload, {
          timeout: 60000 // 60 second timeout for translation (increased for document text)
        });
        debugLog('Translation response', {
          success: response.data.success,
          serviceUsed: response.data.service_used,
          confidence: response.data.confidence_score,
          wasTranslated: response.data.was_translated
        });
        setTranslationResult(response.data);
        translationResultRef.current = response.data;
        setTranslationJustCompleted(true);
        setTimeout(() => setTranslationJustCompleted(false), 3500);
        setTranslateRanThisSession(true);
        // Set the selected translation ID to the new translation
        if (response.data.id) {
          setSelectedTranslationId(response.data.id);
        }
        // Clear audio URL only after new translation is ready
        setInlineAudioUrl(null);
        setShowInlinePlayer(false);
        setTtsProgress({ status: null, queuePosition: null, message: '' });
        setIsNewlyGeneratedAudio(false);
        
        // Refresh translation history to include the new translation
        if (response.data.id) {
          // Expand the new translation immediately
          expandTranslationInHistory(response.data.id);
          fetchTranslationHistory(false, historySearchQuery, historyDateRange);
        }
        
        // Auto-show TTS options if translating to Haitian Creole
        if (targetLanguage === 'ht') {
          setShowTTSOptions(true);

          // ── Real-time playback branch ──────────────────────────────
          // Skip the Generate Speech round-trip: push the freshly
          // translated Creole text directly into Live Stream TTS. The
          // AudioContext was primed at the top of handleTranslate so
          // Safari's autoplay policy is already satisfied.
          if (realtimePlayback && response.data.translated_text) {
            try {
              await runS2SFromText(response.data.translated_text, {
                translationId: response.data.id || null,
              });
              if (window.refreshPlanCredits) window.refreshPlanCredits();
            } catch (rtErr) {
              console.error('[Translate RT text] streaming failed:', rtErr);
              setError(t.err_playback_failed);
            }
          } else if (autoPlayAfterTranslation &&
              response.data.translated_text &&
              response.data.translated_text.length < AUTOPLAY_MAX_CHAR_LENGTH &&
              selectedVoice) {
            // Legacy auto-play (non-streaming): standard Generate Speech
            // pipeline on short outputs.
            debugLog('Auto-playing translation', {
              textLength: response.data.translated_text.length,
              selectedVoice
            });
            setTimeout(() => {
              setIsNewlyGeneratedAudio(true);
              setAutoTriggeredTTS(true);
              autoTriggeredTTSRef.current = true;
              handleTextToSpeech(true, response.data);
            }, 100);
          }
        }
      } // End of else block for text-only translation
      
      if (window.refreshPlanCredits) window.refreshPlanCredits();
    } catch (err) {
      console.error('[handleTranslate] Translation failed:', err);
      console.error('[handleTranslate] Error details:', err.response?.data);
      console.error('[handleTranslate] Error status:', err.response?.status);
      console.error('[handleTranslate] Full error object:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || t.err_translation_failed);
    } finally {
      console.log('[handleTranslate] Finally block - setting isTranslating to false');
      setIsTranslating(false);
    }
  };

  const handleTextToSpeech = async (autoTriggered = false, translationData = null) => {
    // Ensure autoTriggered is a boolean (in case an event is passed by mistake)
    const isAutoTriggered = typeof autoTriggered === 'boolean' ? autoTriggered : false;
    // Use provided translation data or fall back to state
    const translation = translationData || translationResult;
    
    console.log('[Translation] Generate Speech button clicked!', { autoTriggered: isAutoTriggered });
    debugLog('Generate Speech button clicked', {
      hasTranslationResult: !!translation,
      hasTranslatedText: !!translation?.translated_text,
      selectedVoice: selectedVoice,
      translatedTextLength: translation?.translated_text?.length || 0,
      autoTriggered: isAutoTriggered,
      usingProvidedData: !!translationData
    });
    
    if (!translation?.translated_text || !selectedVoice) {
      debugLog('TTS aborted - missing requirements', {
        hasTranslatedText: !!translation?.translated_text,
        hasSelectedVoice: !!selectedVoice
      });
      if (!selectedVoice && !isAutoTriggered) {
        setError(t.err_no_voice);
      }
      return;
    }

    setIsSynthesizing(true);
    setError(null);
    setTtsSuccess(false);
    // Don't clear the audio URL immediately - keep it until new one is ready
    // setShowInlinePlayer(false);
    // setInlineAudioUrl(null);
    setWordTimings(null);
    setHighlightedWordIndex(-1);
    setTtsProgress({
      status: 'starting',
      queuePosition: null,
      message: 'Initializing...'
    });

    const textLength = translation.translated_text.length;
    const isShortText = textLength < AUTOPLAY_MAX_CHAR_LENGTH;
    
    // Show inline player only for short text
    setShowInlinePlayer(isShortText);

    debugLog('TTS submission started', {
      textLength,
      isShortText,
      voice: selectedVoice,
      maxCharLength: AUTOPLAY_MAX_CHAR_LENGTH
    });

    try {
      // Get voice-specific defaults
      const voice = voices.find(v => v.voice_id === selectedVoice);

      // Refuse to submit a voice that isn't in the current My Voices list (e.g.
      // a stale persisted selection). Submitting an unknown voice would fall
      // through to the default VITS model and route to the wrong service. vendor-name-ok: dev-only architectural comment
      if (!voice) {
        debugLog('TTS aborted: selected voice not in My Voices', { selectedVoice });
        setError(t.err_no_voice_from_list);
        setSelectedVoice(voices.length > 0 ? voices[0].voice_id : null);
        setIsSynthesizing(false);
        return;
      }

      // Bracket-tag hard-block for the Translate page. Auto-triggered
      // (auto-play-after-translation) intentionally skips the modal —
      // that path has no click to attach the deletion decision to.
      // Manual Generate Speech routes through the modal every time.
      if (!isAutoTriggered) {
        const _tags = detectEmotionTags(translation.translated_text || '');
        if (_tags.length > 0 && !voiceSupportsEmotionTags(voice)) {
          setShowEmotionTagsModal(true);
          setIsSynthesizing(false);
          return;
        }
      }

      // Route to the correct model/provider based on the voice. Each voice
      // declares which model(s) it supports; the backend dispatcher routes by
      // the model's provider (Gemini for Creole Studio, Infer for VITS), so we vendor-name-ok: dev-only architectural comment
      // must send the model the voice actually belongs to — not a hardcoded one.
      const resolveModelForVoice = (v) => {
        const sm = (v && v.supported_models) || [];
        if (sm.includes('ccl_ht_gem_pro')) return 'ccl_ht_gem_pro';
        if (sm.includes('ccl_ht_gem')) return 'ccl_ht_gem';
        const regional = regionalModelIdOf(sm);
        if (regional) return regional;
        if (sm.length === 1) return sm[0];
        return DEFAULT_MODEL_ID;
      };
      const modelId = resolveModelForVoice(voice);
      debugLog('TTS model routing', { selectedVoice, supported_models: voice?.supported_models, modelId });

      // Submit TTS job with voice-specific defaults
      const requestData = {
        text: translation.translated_text,
        voice_id: selectedVoice,
        model_id: modelId,
        output_format: 'mp3',
        inference_mode: 'standard', // Always use standard mode (1 credit per character)
        include_word_timings: isShortText, // Request word timings for short text
        timing_granularity: 'word', // Word-level timing for highlighting
        translation_id: translation.id || translation.translation_id, // Include translation ID to link TTS to translation
        // override_emotion_tags_warning was removed 2026-09-04 — the
        // server-side guard (bcc860a2) hard-blocks unsupported brackets
        // with no override. If a caller still sets it the backend
        // silently ignores it.

        // Voice-specific settings (use defaults from voice if available)
        speed: voice?.default_speed ?? 0.9,
        stability: voice?.default_stability ?? 0.75,
        similarity_boost: voice?.default_similarity_boost ?? 0.80,
        style: voice?.default_style_strength ?? 0.0,
        use_speaker_boost: voice?.use_speaker_boost ?? true,

        // Pause settings (use voice defaults if available)
        comma_pause_ms: voice?.default_comma_pause_ms ?? 250,
        semicolon_pause_ms: voice?.default_semicolon_pause_ms ?? 300,
        colon_pause_ms: voice?.default_colon_pause_ms ?? 350,
        period_pause_ms: voice?.default_period_pause_ms ?? 500,
        exclamation_pause_ms: voice?.default_exclamation_pause_ms ?? 550,
        question_pause_ms: voice?.default_question_pause_ms ?? 600,
        ellipsis_pause_ms: voice?.default_ellipsis_pause_ms ?? 700,
        paragraph_pause_ms: voice?.default_paragraph_pause_ms ?? 800
      };
      
      debugLog('TTS request data', requestData);
      
      // Add timeout to API request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout
      
      let response;
      try {
        // Always use standard endpoint (1 credit per character)
        const endpoint = '/v1/tts/jobs/';
        debugLog('Using TTS endpoint', { endpoint, mode: 'standard', textLength });

        response = await api.post(endpoint, requestData, {
          signal: controller.signal,
          timeout: 30000 // Override default 10s timeout with 30s for TTS
        });
      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          throw new Error(t.err_request_timeout);
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }
      
      debugLog('TTS response', {
        id: response.data.id,
        idType: typeof response.data.id,
        status: response.data.status,
        audioUrl: response.data.audio_file_url,
        hasAudioUrl: !!response.data.audio_file_url,
        data: response.data
      });

      if (response.data.id) {
        // Ensure we store the ID as a string for consistent comparison
        // SET THIS IMMEDIATELY to catch webhooks that arrive before subscription completes
        const jobId = String(response.data.id);
        console.log('[TTS] Setting currentTtsJobId:', jobId, 'Type:', typeof jobId);
        currentTtsJobIdRef.current = jobId; // Set ref FIRST (synchronous)
        setCurrentTtsJobId(jobId); // Then set state (asynchronous)

        // Subscribe to job updates IMMEDIATELY after setting job ID
        // This ensures webhooks can match the job ID even if they arrive very quickly
        if (subscribeToJob) {
          debugLog('Subscribing to job updates immediately', { jobId });
          subscribeToJob(jobId);
        }

        if (isShortText) {
          // For express, check if we got immediate audio
          if (response.data.audio_file_url && response.data.status === 'completed') {
            // Realtime completed immediately
            debugLog('Realtime TTS completed immediately', { 
              audioUrl: response.data.audio_file_url,
              wordTimings: response.data.word_timings
            });
            setInlineAudioUrl(response.data.audio_file_url);
            setIsSynthesizing(false);
            setShowInlinePlayer(true);
            setIsNewlyGeneratedAudio(true); // This is newly generated audio
            setTtsProgress({
              status: 'completed',
              queuePosition: null,
              message: 'Speech ready!'
            });
            if (response.data.word_timings) {
              setWordTimings(response.data.word_timings);
            }
            
            // Expand the translation in history immediately
            expandTranslationInHistory(selectedTranslationId);
            
            // Refresh translation history
            fetchTranslationHistory(false, historySearchQuery, historyDateRange);
            
            // Clear progress after 5 seconds
            setTimeout(() => {
              setTtsProgress({
                status: null,
                queuePosition: null,
                message: ''
              });
            }, 5000);
          } else {
            // Realtime might be queued or processing
            setTtsProgress({
              status: 'synthesizing',
              queuePosition: null,
              message: 'Processing express audio...'
            });
            // Already subscribed to job updates above
          }
          // Show inline player
          setShowInlinePlayer(true);
          setIsNewlyGeneratedAudio(true); // This is newly generated audio
          
          // Add a timeout to handle stuck jobs
          const timeoutId = setTimeout(() => {
            if (isSynthesizing) {
              debugLog('TTS timeout - job stuck after 30 seconds', { jobId: response.data.id });
              setError(t.err_speech_timeout);
              setIsSynthesizing(false);
              setShowInlinePlayer(false);
              if (unsubscribeFromJob && response.data.id) {
                unsubscribeFromJob(response.data.id);
              }
            }
          }, 30000); // 30 second timeout
          
          // Store timeout ID to clear it if job completes
          window.ttsTimeoutId = timeoutId;
        } else {
          // For long text, update progress - already subscribed to job updates above
          setTtsProgress({
            status: 'queued',
            queuePosition: null,
            message: 'Job queued for processing...'
          });

          // Already subscribed to job updates above
          
          // Add a timeout for long text jobs too
          const longTextTimeoutId = setTimeout(() => {
            if (isSynthesizing) {
              debugLog('Long text TTS timeout - job stuck after 60 seconds', { jobId: response.data.id });
              setError(t.err_speech_slow);
              setIsSynthesizing(false);
              setTtsProgress({
                status: null,
                queuePosition: null,
                message: ''
              });
              if (unsubscribeFromJob && response.data.id) {
                unsubscribeFromJob(response.data.id);
              }
            }
          }, 60000); // 60 second timeout for long text
          
          // Store timeout ID to clear it if job completes
          window.ttsLongTextTimeoutId = longTextTimeoutId;

          // Don't show success here - wait for webhook 'delivered' status
          // The job is just queued at this point
        }
      }
      
      if (window.refreshPlanCredits) window.refreshPlanCredits();
    } catch (err) {
      console.error('TTS failed:', err);
      debugLog('TTS error', {
        error: err.message,
        response: err.response?.data
      });
      setError(err.response?.data?.error || t.err_speech_synth_failed);
      setShowInlinePlayer(false);
      setIsSynthesizing(false);
      setTtsProgress({
        status: null,
        queuePosition: null,
        message: ''
      });
    }
  };

  // Function to estimate transcription cost for audio/voice input
  const estimateTranscriptionCost = async (audioData) => {
    if (!audioData || !authToken) return null;
    
    try {
      const formData = new FormData();
      if (audioData instanceof File) {
        formData.append('audio', audioData);
      } else if (audioData instanceof Blob) {
        // Use webm format to match what MicrophoneRecorder produces
        formData.append('audio', audioData, 'recording.webm');
      }
      formData.append('from_language', sourceLanguage || 'auto');
      if (targetLanguage) {
        formData.append('to_language', targetLanguage);
      }
      
      const response = await api.post('/v1/speech/stt/estimate/', formData);
      
      console.log('STT Estimation API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error estimating transcription cost:', error);
      // Return a fallback estimate based on file size and duration
      if (audioData) {
        const sizeInMB = audioData.size / (1024 * 1024);
        
        // For recorded audio, use the actual recording duration if available
        if (recordingDuration > 0) {
          const estimatedMinutes = recordingDuration / 60;
          // Average speaking rate: 150 words per minute
          const estimatedWords = estimatedMinutes * 150;
          // Average 5.1 characters per word
          const estimatedCharacters = estimatedWords * 5.1;
          
          console.log('Fallback estimation using recording duration:', {
            recordingDuration,
            estimatedMinutes,
            estimatedWords,
            estimatedCharacters
          });
          
          return {
            total_credits: {
              likely: Math.ceil(estimatedCharacters)
            }
          };
        } else {
          // Rough estimate: 1MB of audio ≈ 1 minute for compressed audio
          const estimatedMinutes = sizeInMB;
          // Average speaking rate: 150 words per minute
          const estimatedWords = estimatedMinutes * 150;
          // Average 5.1 characters per word
          const estimatedCharacters = estimatedWords * 5.1;
          
          console.log('Fallback estimation using file size:', {
            sizeInMB,
            estimatedMinutes,
            estimatedWords,
            estimatedCharacters
          });
          
          return {
            total_credits: {
              likely: Math.ceil(estimatedCharacters)
            }
          };
        }
      }
      return null;
    }
  };

  // Effect to update transcription cost estimate when audio changes
  useEffect(() => {
    const updateEstimate = async () => {
      const audioData = recordedAudio || audioFile;
      if (audioData && (inputMode === 'voice' || inputMode === 'document')) {
        // Don't overwrite actual cost breakdown with estimate
        if (typeof transcriptionCost === 'object' && transcriptionCost?.total !== undefined) {
          return; // Keep the actual cost, don't replace with estimate
        }

        const estimate = await estimateTranscriptionCost(audioData);
        if (estimate) {
          setTranscriptionCost(estimate.total_credits?.likely || null);
        }
      } else {
        // Only clear if not an actual result (object)
        if (typeof transcriptionCost !== 'object') {
          setTranscriptionCost(null);
        }
      }
    };

    updateEstimate();
  }, [recordedAudio, audioFile, sourceLanguage, targetLanguage, inputMode]);

  // Define scroll threshold constant
  const SCROLL_DISMISS_THRESHOLD = 300; // Pixels of scrolling required to dismiss

  // Scroll event listener to dismiss audio player with threshold
  useEffect(() => {
    if (!currentlyPlayingAudio) return;

    const handleScroll = (event) => {
      // Ignore scrolls within the audio player itself
      if (audioPlayerContainerRef.current && !audioPlayerContainerRef.current.contains(event.target)) {
        // Track current scroll position
        const currentScrollPos = event.target === window
          ? window.pageYOffset || document.documentElement.scrollTop
          : event.target.scrollTop;

        // Initialize start position on first scroll
        if (scrollStartPosRef.current === null) {
          scrollStartPosRef.current = currentScrollPos;
        }

        // Calculate scroll distance
        const scrollDistance = Math.abs(currentScrollPos - scrollStartPosRef.current);

        // Only dismiss if scrolled more than threshold
        if (scrollDistance > SCROLL_DISMISS_THRESHOLD) {
          // Dismiss the player
          setCurrentlyPlayingAudio(null);
          setHighlightedWordIndex(-1);
          // Reset start position
          scrollStartPosRef.current = null;
        }
      }
    };

    // Reset scroll start position when audio starts playing
    scrollStartPosRef.current = null;

    // Add scroll listener to the main container or window
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Find all scrollable elements and attach listeners
    const scrollableElements = document.querySelectorAll('.overflow-y-auto, .overflow-y-scroll');
    scrollableElements.forEach(element => {
      // Don't add listener to the audio player itself
      if (!audioPlayerContainerRef.current?.contains(element)) {
        element.addEventListener('scroll', handleScroll, { passive: true });
      }
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollableElements.forEach(element => {
        element.removeEventListener('scroll', handleScroll);
      });
    };
  }, [currentlyPlayingAudio]);

  // Listen for sidebar state changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'sidebarPinned') {
        setSidebarPinned(e.newValue === 'true');
      }
    };
    
    // Also listen for custom event that might be dispatched by sidebar
    const handleSidebarToggle = () => {
      setSidebarPinned(localStorage.getItem('sidebarPinned') === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    
    // Check periodically in case storage event doesn't fire
    const interval = setInterval(() => {
      const currentState = localStorage.getItem('sidebarPinned') === 'true';
      setSidebarPinned(prev => {
        if (prev !== currentState) {
          return currentState;
        }
        return prev;
      });
    }, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
      clearInterval(interval);
    };
  }, []);

  // Calculate costs - New pricing: 1 credit per character
  const translationCost = (inputMode === 'text' || inputMode === 'text_document')
    ? (inputText.length || 0)  // 1 credit per character for text/document translation (no transcription)
    : (typeof transcriptionCost === 'object' && transcriptionCost?.total !== undefined
        ? transcriptionCost.total
        : (transcriptionCost || 0));  // For voice/audio modes, calculated after transcription
  const ttsCost = translationResult ? translationResult.translated_text.length : 0;  // 1 credit per character for TTS (Standard mode)

  // Audio player handlers (handleTimeUpdate and handleAudioEnded)
  const handleTimeUpdate = useCallback((currentTime) => {
    const timings = currentlyPlayingAudio?.word_timings || wordTimings;
    if (timings && timings.length > 0) {
      let wordIndex = -1;
      for (let i = 0; i < timings.length; i++) {
        const timing = timings[i];
        const nextTiming = timings[i + 1];
        
        if (currentTime >= timing.start_time && 
            (!nextTiming || currentTime < nextTiming.start_time)) {
          wordIndex = i;
          break;
        }
      }
      
      if (wordIndex !== -1) {
        if (wordIndex !== highlightedWordIndex) {
          setHighlightedWordIndex(wordIndex);
        }
      } else if (highlightedWordIndex !== -1) {
        setHighlightedWordIndex(-1);
      }
    }
  }, [currentlyPlayingAudio, wordTimings, highlightedWordIndex]);

  const handleAudioEnded = useCallback(() => {
    const itemId = currentlyPlayingAudio?.item_id;
    setHighlightedWordIndex(-1);
    setIsPlaying(false);
    // Reset autoplay flag so next manual play doesn't auto-start
    setShouldAutoPlay(false);
    // Clear saved audio position since playback completed
    if (itemId) {
      console.log(`[Translation] Clearing saved audio position for item ${itemId} (playback completed)`);
      setSavedAudioPositions(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }
    // The AudioPlayerWithDuration component will handle resetting internally
    // Don't clear currentlyPlayingAudio - keep the player visible
    // User can replay by clicking play button
  }, [currentlyPlayingAudio]);

  // Show loading spinner while checking authentication
  if (!isAuthenticated || authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Extracted so we can render it either below Step 3 (stacked view)
  // OR inside the right-pane History tab (split view) without
  // duplicating the ~45-line block of props.
  const historyCardEl = (
    <div ref={historyCardRef}>
      <TranslationHistoryCard
        translationHistory={translationHistory}
        isLoadingHistory={isLoadingHistory}
        historyTotalCount={historyTotalCount}
        historyCurrentPage={historyCurrentPage}
        historySearchQuery={historySearchQuery}
        historyDateRange={historyDateRange}
        showDatePicker={showDatePicker}
        selectedTranslationId={selectedTranslationId}
        showTranslatedText={showTranslatedText}
        showHistoryMenus={showHistoryMenus}
        historyHasMore={historyHasMore}
        setHistorySearchQuery={setHistorySearchQuery}
        setHistoryDateRange={setHistoryDateRange}
        setShowDatePicker={setShowDatePicker}
        setSelectedTranslationId={setSelectedTranslationId}
        setShowTranslatedText={setShowTranslatedText}
        setShowHistoryMenus={setShowHistoryMenus}
        setHistoryCurrentPage={setHistoryCurrentPage}
        fetchTranslationHistory={fetchTranslationHistory}
        setTranslationHistory={setTranslationHistory}
        handleHistoryItemAction={handleHistoryItemAction}
        debugLog={debugLog}
        voices={voices}
        supportedLanguages={supportedLanguages}
        engineOptions={engineOptions}
        copyToClipboard={copyToClipboard}
        formatProcessingTime={formatProcessingTime}
        getAudioProxyUrl={getAudioProxyUrl}
        playAudio={playAudio}
        currentlyPlayingAudio={currentlyPlayingAudio}
        isPlaying={isPlaying}
        stopAudio={stopAudio}
        pauseAudio={pauseAudio}
        resumeAudio={resumeAudio}
        setInputText={setInputText}
        expandedRows={expandedHistoryRows}
        setExpandedRows={setExpandedHistoryRows}
      />
    </div>
  );

  // Right-pane tab bar + body for split view. Mirrors the TTS/STT
  // pattern (Options on the left, History centered, Settings on the
  // right). lg:pt-8 reserves vertical space in split view so the
  // below-header LanguageSwitcher (absolute top-20 right-4 of the
  // outer header) doesn't overlap the Settings tab at the right edge.
  const rightPaneEl = (
    <>
      <div className="border-b dark:border-dark-border lg:pt-8">
        <div className="flex justify-between px-4 sm:px-6">
          <div className="flex">
            <button
              type="button"
              onClick={() => setActiveRightTab('options')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeRightTab === 'options'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {t.tab_options}
            </button>
            <button
              type="button"
              onClick={() => setActiveRightTab('history')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeRightTab === 'history'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {t.tab_history}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setActiveRightTab('settings')}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
              activeRightTab === 'settings'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {t.tab_settings}
          </button>
        </div>
      </div>
      <div className="flex-1 lg:overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
        {activeRightTab === 'history' && historyCardEl}
        {activeRightTab === 'options' && (
          <TranslateOptionsTab
            voices={voices}
            selectedVoice={selectedVoice}
            setSelectedVoice={setSelectedVoice}
            translationEngine={translationEngine}
            setTranslationEngine={setTranslationEngine}
            engineOptions={engineOptions}
            hasEngineAccess={hasEngineAccess}
            realtimeVoicesProOnly={realtimePlayback && targetLanguage === 'ht' && hasProAccess()}
            IOSSwitch={IOSSwitch}
          />
        )}
        {activeRightTab === 'settings' && (
          <TranslateSettingsTab
            realtimePlayback={realtimePlayback && targetLanguage === 'ht' && hasProAccess()}
            setRealtimePlayback={setRealtimePlayback}
            realtimeToggleDisabled={targetLanguage !== 'ht' || !hasProAccess()}
            realtimeDisabledReason={
              targetLanguage !== 'ht'
                ? 'Change Target to Haitian Creole to enable Real-time playback.'
                : !hasProAccess()
                  ? 'Real-time playback runs on Creole Studio Pro voices — Creator plan or above.'
                  : ''
            }
            onRealtimeChange={(checked) => {
              // Prime the AudioContext inside this user-gesture window so
              // Safari lets us play audio later without a manual re-tap.
              if (checked) {
                try { liveStream.prime(); } catch (err) { console.warn('[Translate] liveStream.prime() failed at Settings toggle:', err?.message || err); }
              }
              setRealtimePlayback(checked);
            }}
            autoPlayAfterTranslation={autoPlayAfterTranslation}
            setAutoPlayAfterTranslation={setAutoPlayAfterTranslation}
            IOSSwitch={IOSSwitch}
          />
        )}
      </div>
    </>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-dark-bg">
      <SEO
        title={t.seo_title}
        description={t.seo_description}
        path="/translate"
        lang={language}
      />
      {/* Mobile scrollable container - header scrolls with content */}
      <div className="h-full overflow-y-auto lg:overflow-hidden lg:flex lg:flex-col">
        {/* Header - scrolls on mobile, fixed on desktop */}
        <div className="border-b border-gray-400 dark:border-dark-border bg-gray-200 dark:bg-dark-card shadow-md lg:flex-shrink-0 relative">
          <LanguageSwitcher variant="below-header" />
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{t.h_title}</h1>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-400 mt-1">
                  {t.h_subtitle}
                </p>
              </div>
              <div className="self-center sm:self-auto flex items-center gap-3">
                {/* View toggle — hidden on mobile since split view collapses
                    to a single column below `lg`. Persists via
                    translate_viewMode in localStorage. */}
                <button
                  type="button"
                  onClick={() => setViewMode(viewMode === 'split' ? 'stacked' : 'split')}
                  className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-card text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title={viewMode === 'split' ? t.view_stacked_title : t.view_split_title}
                  aria-label={viewMode === 'split' ? t.view_stacked_aria : t.view_split_aria}
                >
                  {viewMode === 'split' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v18M4 5h16v14H4z" />
                    </svg>
                  )}
                  <span>{viewMode === 'split' ? t.view_stacked_label : t.view_split_label}</span>
                </button>
                <PlanCreditsDisplay />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content — stacked (single column) or split (left steps
            + resizable right pane of tabs). Split collapses to stacked
            below `lg`. */}
        <div className={viewMode === 'split' ? 'flex-1 lg:overflow-hidden' : 'flex-1 lg:overflow-y-auto'}>
        <div
          ref={viewMode === 'split' ? splitContainerRef : null}
          className={viewMode === 'split' ? 'flex flex-col lg:flex-row h-full relative' : ''}
        >
        <div
          className={
            viewMode === 'split'
              ? 'flex flex-col bg-transparent overflow-hidden w-full lg:w-auto lg:overflow-y-auto'
              : ''
          }
          style={
            viewMode === 'split' && typeof window !== 'undefined' && window.innerWidth >= 1024
              ? { width: `calc(${dividerPosition}% - 0.5rem)` }
              : undefined
          }
        >
          <div className="py-4 sm:py-6 px-0 sm:px-[10pt]">
          {/* Step 1 - Mode Selection */}
          <TranslationStep1Input
            inputMode={inputMode}
            setInputMode={setInputMode}
            sourceLanguage={sourceLanguage}
            setSourceLanguage={setSourceLanguage}
            hasVoiceAccess={hasVoiceAccess}
          />

          {/* Speech-to-Speech "Real-time playback" toggle. Placed here so
              it's discoverable regardless of layout / sub-component. Only
              interactive when target is Haitian Creole (the streaming
              Voice Library is Creole-only); greyed out with a hint
              otherwise so users see it exists. */}
          <div className="mx-4 sm:mx-6 mb-3 p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
            <label
              className={`flex items-start gap-2 text-sm select-none ${
                targetLanguage === 'ht' && hasProAccess()
                  ? 'text-gray-800 dark:text-gray-200 cursor-pointer'
                  : 'text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
              title={targetLanguage !== 'ht'
                ? t.rt_title_needs_target
                : !hasProAccess()
                  ? t.rt_title_needs_pro
                  : inputMode === 'voice'
                    ? t.rt_title_voice
                    : inputMode === 'document'
                      ? t.rt_title_document
                      : inputMode === 'text_document'
                        ? t.rt_title_text_document
                        : t.rt_title_text}
            >
              <input
                type="checkbox"
                checked={realtimePlayback && targetLanguage === 'ht' && hasProAccess()}
                onChange={(e) => {
                  // Prime the AudioContext inside this user gesture. Mic
                  // Stop fires as an async MediaRecorder event (NOT a
                  // gesture context) — priming here guarantees Safari
                  // allows playback later without a manual "tap to enable
                  // audio" fallback.
                  if (e.target.checked) {
                    try { liveStream.prime(); } catch (err) { console.warn('[Translate] liveStream.prime() failed at Real-time toggle:', err?.message || err); }
                  }
                  setRealtimePlayback(e.target.checked);
                }}
                disabled={targetLanguage !== 'ht' || !hasProAccess()}
                className="w-4 h-4 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 disabled:opacity-40"
              />
              <span className="flex-1 flex items-center gap-1 flex-wrap">
                <span className="font-medium">🎙️ {t.rt_label}</span>
                <span className="text-[10px] uppercase tracking-wide bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                  {t.rt_pro_badge}
                </span>
                {targetLanguage !== 'ht' ? (
                  <span className="text-gray-500 dark:text-gray-400 text-xs italic">
                    {t.rt_needs_target}
                  </span>
                ) : !hasProAccess() ? (
                  <span className="text-gray-500 dark:text-gray-400 text-xs italic">
                    {t.rt_needs_pro}
                  </span>
                ) : inputMode === 'voice' ? (
                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                    {t.rt_hint_voice}
                  </span>
                ) : inputMode === 'document' ? (
                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                    {t.rt_hint_document}
                  </span>
                ) : inputMode === 'text_document' ? (
                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                    {t.rt_hint_text_document}
                  </span>
                ) : (
                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                    {t.rt_hint_text}
                  </span>
                )}
              </span>
            </label>
            {interimSpeechText && !liveStream.isStreamingLive && (
              <div className="mt-2 p-2 rounded-md bg-white/70 dark:bg-gray-900/40 border border-purple-100 dark:border-purple-800 text-xs text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-purple-700 dark:text-purple-300 mr-1">{t.rt_buffering_label}</span>
                <span className="italic">{interimSpeechText.slice(0, 240)}{interimSpeechText.length > 240 ? '…' : ''}</span>
              </div>
            )}
            {liveStream.isStreamingLive && (
              <button
                type="button"
                onClick={async () => {
                  if (liveStream.streamingJobId) {
                    try {
                      await api.post(
                        `/v1/tts/jobs/${liveStream.streamingJobId}/cancel-stream/`,
                        {},
                        { headers: { Authorization: `Bearer ${authToken}` } }
                      );
                    } catch (err) {
                      console.warn('[Translate S2S] cancel-stream POST failed:', err);
                    }
                  }
                  liveStream.stop();
                  setIsTranslating(false);
                }}
                className="mt-2 w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <rect x="5" y="5" width="10" height="10" rx="1" />
                </svg>
                {t.stop_streaming}
              </button>
            )}
          </div>

          {/* Step 2 - Translation Settings */}
          <TranslationStep2Language
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            translationEngine={translationEngine}
            isEngineCollapsed={isEngineCollapsed}
            planLoading={planLoading}
            translationResult={translationResult}
            inputText={inputText}
            isTranslating={isTranslating}
            error={error}
            inputMode={inputMode}
            supportedLanguages={supportedLanguages}
            engineOptions={engineOptions}
            autoSelectedEngine={autoSelectedEngine}
            setSourceLanguage={setSourceLanguage}
            setTargetLanguage={setTargetLanguage}
            setTranslationEngine={setTranslationEngine}
            setIsEngineCollapsed={setIsEngineCollapsed}
            handleTranslate={handleTranslate}
            hasEngineAccess={hasEngineAccess}
          />

          {/* Step 3 - Input Content (context-aware based on mode) */}
          <TranslationStep2InputContent
            inputMode={inputMode}
            inputText={inputText}
            setInputText={setInputText}
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            selectedTranslationId={selectedTranslationId}
            setSelectedTranslationId={setSelectedTranslationId}
            setTranslationResult={setTranslationResult}
            setShowTTSOptions={setShowTTSOptions}
            setShowInlinePlayer={setShowInlinePlayer}
            setInlineAudioUrl={setInlineAudioUrl}
            setTtsProgress={setTtsProgress}
            setIsNewlyGeneratedAudio={setIsNewlyGeneratedAudio}
            setError={setError}
            setCurrentTtsJobId={setCurrentTtsJobId}
            recordedAudio={recordedAudio}
            setRecordedAudio={setRecordedAudio}
            recordingDuration={recordingDuration}
            setRecordingDuration={setRecordingDuration}
            audioFile={audioFile}
            setAudioFile={setAudioFile}
            authToken={authToken}
            transcriptionCost={transcriptionCost}
            setTranscriptionCost={setTranscriptionCost}
            maxCharsLimit={maxCharsLimit}
            translationCost={translationCost}
            sanitizeText={sanitizeText}
            formatCharCount={formatCharCount}
            hasVoiceAccess={hasVoiceAccess}
            debugLog={debugLog}
            isTranslating={isTranslating}
            handleTranslate={handleTranslate}
            translationResult={translationResult}
            realtimePlayback={realtimePlayback}
            targetLanguage={targetLanguage}
            onRealtimeAudioReady={handleRealtimeAudioReady}
            onRealtimeRecordingStart={handleRealtimeRecordingStart}
            onBackgroundSpeechChunk={runBackgroundSpeechChunk}
            onInterimSpeechText={setInterimSpeechText}
            translationSuccess={translationJustCompleted}
            isTranscribing={isTranscribing}
            showTranscriptionSuccess={showTranscriptionSuccess}
            showStep3Options={showStep3Options}
            setShowStep3Options={setShowStep3Options}
            selectedMicrophoneLabel={selectedMicrophoneLabel}
            volumeBoost={volumeBoost}
            highlightedWords={highlightedWords}
          />

          {/* Step 4 - TTS Options */}
          <TranslationStep3TTS
            targetLanguage={targetLanguage}
            hideGenerateSpeech={realtimePlayback && targetLanguage === 'ht'}
            showTTSOptions={showTTSOptions}
            selectedVoice={selectedVoice}
            autoPlayAfterTranslation={autoPlayAfterTranslation}
            translationResult={translationResult}
            inputText={inputText}
            translateRanThisSession={translateRanThisSession}
            voices={voices}
            realtimeVoicesProOnly={realtimePlayback && targetLanguage === 'ht' && hasProAccess()}
            isSynthesizing={isSynthesizing}
            ttsProgress={ttsProgress}
            ttsSuccess={ttsSuccess}
            audioUrl={audioUrl}
            error={error}
            ttsCost={ttsCost}
            setShowTTSOptions={setShowTTSOptions}
            setSelectedVoice={setSelectedVoice}
            setAutoPlayAfterTranslation={setAutoPlayAfterTranslation}
            handleTextToSpeech={handleTextToSpeech}
            setTtsSuccess={setTtsSuccess}
            setTtsProgress={setTtsProgress}
            IOSSwitch={IOSSwitch}
          />

          {/* Translation History — rendered inline in stacked view;
              in split view it lives in the right-pane History tab. */}
          {viewMode === 'stacked' && historyCardEl}
          </div>
        </div>
        {/* Split-view: adjustable divider + right pane. Both hidden
            below `lg` so mobile collapses to the stacked layout above. */}
        {viewMode === 'split' && (
          <>
            <div
              className="hidden lg:block relative w-1 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 dark:hover:bg-blue-400 cursor-col-resize transition-colors"
              onMouseDown={handleSplitMouseDown}
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 pointer-events-none">
                <div className="w-1 h-1 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
              </div>
            </div>
            <div
              className="flex flex-col bg-white dark:bg-dark-card overflow-hidden w-full lg:w-auto border-t lg:border-t-0 lg:border-l dark:border-gray-700"
              style={
                typeof window !== 'undefined' && window.innerWidth >= 1024
                  ? { width: `calc(${100 - dividerPosition}% - 0.5rem)` }
                  : undefined
              }
            >
              {rightPaneEl}
            </div>
          </>
        )}
        </div>
        </div>

      {/* All modals and popups */}
      {/* Bracket-tag hard-block modal — fires when the translated text
          contains [laughs]/[whispers]-style tags AND the picked voice
          is Regional (VITS, which would read them literally). Two vendor-name-ok: dev-only architectural comment
          remedies: switch to Creole Studio, or delete the brackets
          and generate. No "Proceed anyway" — the backend rejects
          these submissions regardless. */}
      <EmotionTagsConfirmModal
        open={showEmotionTagsModal}
        tags={detectEmotionTags(translationResult?.translated_text || '')}
        onCancel={() => setShowEmotionTagsModal(false)}
        onSwitchToStudio={() => {
          setShowEmotionTagsModal(false);
          const studio = (voices || []).find(v => (v.supported_models || []).includes('ccl_ht_gem'));
          if (studio) setSelectedVoice(studio.voice_id);
        }}
        onDeleteAndGenerate={() => {
          // Strip every bracket span from the translated text — VITS vendor-name-ok: dev-only architectural comment
          // has no whitelist so ALL brackets are unsupported here.
          const src = translationResult?.translated_text || '';
          const stripped = stripUnsupportedBrackets(
            src,
            classifyBracketMatches(src).map(m => ({ ...m, kind: 'unsupported' })),
          );
          if (stripped !== src) {
            // Mutate the in-memory translation so the resubmit picks
            // up the cleaned text.
            setTranslationResult(prev =>
              prev ? { ...prev, translated_text: stripped } : prev
            );
          }
          setShowEmotionTagsModal(false);
          setTimeout(() => handleTextToSpeech(), 0);
        }}
      />

      {/* Unified Export Modal for Document (Export button) */}
      {showExportModal && translationResult?.tts_job_details && (
        <UnifiedExportModal
          job={translationResult.tts_job_details}
          jobType="translation"
          authToken={authToken}
          initialTab="document"  // Open on document tab for export
          onClose={() => setShowExportModal(false)}
          onExport={(settings) => {
            console.log('Export document settings:', settings)
            // Export will be handled by the modal itself
          }}
        />
      )}

      {/* Unified Export Modal for Audio (Download button) */}
      {showDownloadModal && translationResult?.tts_job_details && (
        <UnifiedExportModal
          job={{
            ...translationResult.tts_job_details,
            audio_url: translationResult.tts_job_details.audio_url || translationResult.tts_job_details.audio_file_url,
            translated_text: translationResult.translated_text,
            id: translationResult.tts_job_details.id || translationResult.id
          }}
          jobType="translation"
          authToken={authToken}
          initialTab="audio"  // Open on audio tab for download
          onClose={() => setShowDownloadModal(false)}
          onExport={(settings) => {
            console.log('Export audio settings:', settings)
            // Export will be handled by the modal itself
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Entry</h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this entry? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                         transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAudio}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg
                         hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Entry Confirmation Dialog */}
      {showDeleteEntryConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Entry</h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this entry? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteEntryConfirm(false);
                  setEntryToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                         transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg
                         hover:bg-red-700 transition-colors"
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Bottom Sliding Audio Player */}
      {currentlyPlayingAudio && (
        <div
          ref={audioPlayerContainerRef}
          className={`fixed bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg z-50 transition-transform duration-300 ease-in-out ${
            currentlyPlayingAudio ? 'translate-y-0 bottom-0' : 'translate-y-full bottom-0'
          }`}
          style={{ 
            left: typeof window !== 'undefined' && window.innerWidth >= 1024 ? (sidebarPinned ? '256px' : '64px') : '0',
            right: '0',
            bottom: 0
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative py-3 px-4 sm:px-6">
            {/* Close button - positioned in upper right corner */}
            <button
              onClick={() => {
                stopAudio()
              }}
              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t.close_player}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Audio Player - full width */}
            <div className="pr-10">
              <AudioPlayerWithDuration
                ref={sharedAudioPlayerRef}
                audioUrl={currentlyPlayingAudio ? getAudioProxyUrl(currentlyPlayingAudio.audio_url) : ''}
                duration={currentlyPlayingAudio?.duration || 0}
                className="w-full"
                playerId="shared-translation-player"
                showSpeedControl={true}
                recordedSpeed={1.0}
                autoPlay={shouldAutoPlay}
                initialTime={currentlyPlayingAudio?.item_id ? (savedAudioPositions[currentlyPlayingAudio.item_id] || 0) : 0}
                onPlay={() => {
                  console.log('[Translation] Audio started playing');
                  setIsPlaying(true);
                  // Reset the auto-play flag after it's been used
                  if (shouldAutoPlay) {
                    setShouldAutoPlay(false);
                  }
                }}
                onPause={() => {
                  console.log('[Translation] Audio paused');
                  // Save the current audio position before pausing
                  if (currentlyPlayingAudio?.item_id && sharedAudioPlayerRef.current) {
                    const currentTime = sharedAudioPlayerRef.current.currentTime || 0;
                    console.log(`[Translation] Saving audio position for item ${currentlyPlayingAudio.item_id}:`, currentTime);
                    setSavedAudioPositions(prev => ({
                      ...prev,
                      [currentlyPlayingAudio.item_id]: currentTime
                    }));
                  }
                  setIsPlaying(false);
                }}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleAudioEnded}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}